import React, { useState, useEffect, useLayoutEffect, useCallback, useMemo } from 'react';
import {
    Users, BarChart3, Search, RefreshCw, ExternalLink, CheckCircle, XCircle,
    AlertTriangle, Clock, MousePointerClick, Eye, Monitor, Smartphone,
    TrendingUp, ArrowUpRight, Activity, Globe, Layers, FileText,
    Phone, Building2, Mail, ChevronRight, X, MessageSquare,
    Send, ArrowRightLeft, StickyNote, Play, UserCheck, UserX, Loader2,
    CalendarDays, Timer, RotateCcw, ChevronDown, ChevronUp, ClipboardList, BrainCircuit,
    MailCheck, MailX, Repeat, FolderOpen, Save, Shield, Mailbox,
    Gauge, KeyRound, Info, Trash2, Upload, Download, PlayCircle, Square, CheckSquare, Check,
    Pencil, Copy, Tag, Wrench, Sparkles
} from 'lucide-react';
import axiosService from './services/axiosService';
import Tooltip from './components/Tooltip';
import Modal from './components/Modal';
import CsvImportModal from './components/CsvImportModal';
import Tab from './components/Tab';
import Card from './components/Card';
import Button from './components/Button';
import Input from './components/Input';
import Badge from './components/Badge';
import { Link, useNavigate } from 'react-router-dom';
import { useToast } from './ToastContext';
import { getOnboardingStatus } from './api';

import { API_BASE_URL as API_BASE } from './config/api';

/** Lien audit depuis l’admin : toujours marqué staff pour ne pas polluer le tracking (UTM listes / GA). */
const STAFF_AUDIT_QUERY = '?utm_source=staff';

// Tunnel simplifié : Nouveau → En cours → Converti / Perdu (contacted conservé pour l’affichage des anciennes données)
const LEAD_STATUSES = {
    new: { bg: '#e2e8f0', text: '#415069', label: 'Nouveau' },
    contacted: { bg: '#fef3c7', text: '#b45309', label: 'En cours' },
    in_progress: { bg: '#fef3c7', text: '#b45309', label: 'En cours' },
    converted: { bg: '#dcfce7', text: '#16a34a', label: 'Converti' },
    lost: { bg: '#f1f5f9', text: '#94a3b8', label: 'Perdu' },
};
const LEAD_STATUS_OPTIONS = ['new', 'in_progress', 'converted', 'lost'];

const STATUS_MAP = {
    done: { bg: '#dcfce7', text: '#16a34a', label: 'Terminé' },
    processing: { bg: '#dbeafe', text: '#2563eb', label: 'En cours' },
    error: { bg: '#fee2e2', text: '#ef4444', label: 'Erreur' },
    pending: { bg: '#f1f5f9', text: '#94a3b8', label: 'En attente' },
};

// Libellés lisibles pour le tracking (timeline, CTA, etc.)
const EVENT_LABEL_DISPLAY = {
    result_page_view: 'Vue page résultat de l\'audit',
    return_visit: 'Visite retour sur la page résultat',
    session_end: 'Fin de session',
    tab_synthese: 'Clic onglet Synthèse',
    tab_seo: 'Clic onglet SEO',
    tab_performance: 'Clic onglet Performance',
    tab_technique: 'Clic onglet Technique',
    tab_marche: 'Clic onglet Visibilité Google',
    tab_ia: 'Clic onglet Analyse IA',
    tab_errors: 'Clic onglet Erreurs',
    cta_sticky_bar: 'Clic CTA barre sticky',
    cta_blurred_synthese_rdv: 'Clic CTA Prendre RDV (zone floutée synthèse)',
    cta_blurred_marche: 'Clic CTA Prendre RDV (zone floutée marché)',
    cta_blurred_seo_headings: 'Clic CTA Prendre RDV (zone floutée SEO)',
    cta_blurred_performance: 'Clic CTA Prendre RDV (zone floutée performance)',
    cta_blurred_technique: 'Clic CTA Prendre RDV (zone floutée technique)',
    cta_blurred_ia_llm: 'Clic CTA Prendre RDV (zone floutée visibilité IA)',
    cta_marche_teaser: 'Clic CTA audit complet (teaser marché)',
    conversion_modal_cta: 'Clic CTA modal conversion',
    page_view: 'Vue de page',
    form_submit: 'Soumission formulaire',
    landing_page_view: 'Vue page d\'accueil landing',
    landing_step_2_view: 'Vue étape 2 (saisie URL)',
    landing_step_1_url: 'Soumission URL (étape 1)',
    landing_form_contact_submit: 'Envoi formulaire de contact',
    landing_form_contact_success: 'Formulaire contact envoyé avec succès',
    landing_form_contact_error: 'Erreur envoi formulaire contact',
    landing_step_2_back: 'Retour étape 1 depuis étape 2',
    landing_modal_privacy_open: 'Ouverture politique de confidentialité',
    landing_exit: 'Sortie de la landing',
    marche_blocked_audit_complet: 'Clic CTA audit complet (marché bloqué)',
    cta_full_audit: 'Clic CTA audit complet',
    cta_appointment: 'Clic CTA prise de rendez-vous',
};
function getEventLabelDisplay(label) {
    if (!label) return label;
    const known = EVENT_LABEL_DISPLAY[label];
    if (known) return known;
    const scrollMatch = String(label).match(/^scroll_(\d+)$/);
    return scrollMatch ? `Défilement ${scrollMatch[1]} %` : label;
}

/** Durée en secondes → j / h / min / s (lisible pour le commercial). */
function formatDuration(seconds) {
    const s = Math.max(0, Math.round(Number(seconds) || 0));
    const days = Math.floor(s / 86400);
    let rest = s % 86400;
    const h = Math.floor(rest / 3600);
    rest %= 3600;
    const m = Math.floor(rest / 60);
    const sec = rest % 60;
    const parts = [];
    if (days > 0) parts.push(`${days} j`);
    if (h > 0) parts.push(`${h} h`);
    if (m > 0) parts.push(`${m} min`);
    if (sec > 0 || parts.length === 0) parts.push(`${sec} s`);
    return parts.join(' ');
}

function formatProspectEventDateTime(iso) {
    if (!iso) return '';
    try {
        return new Date(iso).toLocaleString('fr-FR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    } catch {
        return '';
    }
}

/** Pour chaque `return_visit`, écart en secondes depuis le `session_end` précédent (même audit). */
function buildReturnVisitGapSecondsMap(timeline) {
    const map = {};
    if (!timeline?.length) return map;
    let lastSessionEndMs = null;
    for (const ev of timeline) {
        if (ev.event_type === 'session_end' && ev.timestamp) {
            const t = new Date(ev.timestamp).getTime();
            if (!Number.isNaN(t)) lastSessionEndMs = t;
        }
        if (ev.event_type === 'return_visit' && ev.timestamp && lastSessionEndMs != null) {
            const t = new Date(ev.timestamp).getTime();
            if (!Number.isNaN(t) && t >= lastSessionEndMs) {
                map[ev.id] = (t - lastSessionEndMs) / 1000;
            }
        }
    }
    return map;
}

// ── Helpers ──

/** Prénom + nom sans doublon (évite « Prénom Prénom » si nom vide et `name` ne fait que répéter le prénom). */
function formatProspectDisplayName(item) {
    if (!item) return '–';
    const fn = (item.first_name || '').trim();
    const ln = (item.last_name || '').trim();
    const nm = (item.name || '').trim();
    if (ln) {
        const joined = [fn, ln].filter(Boolean).join(' ').trim();
        return joined || nm || '–';
    }
    if (nm) return nm;
    if (fn) return fn;
    return '–';
}

function QuotaProgressBar({ used = 0, limit = 1, label = 'Quota' }) {
    const limitSafe = Math.max(1, Number(limit) || 1);
    const usedNum = Math.max(0, Number(used) || 0);
    const pct = Math.min(100, Math.round((usedNum / limitSafe) * 100));
    const color = pct >= 80 ? '#ef4444' : pct >= 70 ? '#eab308' : '#22c55e';
    return (
        <div className="w-full">
            <div className="flex justify-between text-xs font-medium text-blue-gray-600 mb-1">
                <span>{label}</span>
                <span>{usedNum.toLocaleString()} / {limitSafe.toLocaleString()} ({pct}%)</span>
            </div>
            <div className="h-2.5 w-full rounded-full bg-blue-gray-100 overflow-hidden">
                <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{ width: `${pct}%`, backgroundColor: color }}
                />
            </div>
        </div>
    );
}

function StatCard({ icon: Icon, label, value, color = '#415069', sub }) {
    return (
        <div className="bg-white/60 backdrop-blur-sm rounded-lg p-5 border border-blue-gray-100 shadow-sm flex flex-col gap-1">
            <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${color}14` }}>
                    <Icon size={16} style={{ color }} />
                </div>
                <span className="text-xs font-medium text-blue-gray-400">{label}</span>
            </div>
            <div className="text-2xl font-extrabold text-blue-gray-900">{value}</div>
            {sub && <div className="text-xs text-blue-gray-400">{sub}</div>}
        </div>
    );
}

function ScorePill({ score, showDashWhenZero = false }) {
    const v = Math.round(score || 0);
    if (showDashWhenZero && v === 0) return <span className="text-blue-gray-300 text-sm">–</span>;
    const color = v >= 90 ? '#3bb77e' : v >= 60 ? '#d97706' : '#ef4444';
    return <span className="font-bold text-sm" style={{ color }}>{v}/100</span>;
}

function LeadStatusBadge({ status, onClick }) {
    const st = LEAD_STATUSES[status] || LEAD_STATUSES.new;
    return (
        <button
            onClick={onClick}
            className="px-2.5 py-1 rounded-full text-[11px] font-semibold transition-all hover:opacity-80"
            style={{ background: st.bg, color: st.text }}
        >
            {st.label}
        </button>
    );
}

// ── Detail Panel ──

function scoreCardStyle(v) {
    const n = Math.round(Number(v) || 0);
    const color = n >= 90 ? '#3bb77e' : n >= 60 ? '#d97706' : '#ef4444';
    const bg = n >= 90 ? '#f0fdf4' : n >= 60 ? '#fffbeb' : '#fef2f2';
    const border = n >= 90 ? '#bbf7d0' : n >= 60 ? '#fde68a' : '#fecaca';
    return { n, color, bg, border };
}

function ContactDetailRow({ label, value, href, copyValue, icon: RowIcon }) {
    const raw = value != null && value !== '' ? String(value) : '';
    const empty = !raw.trim();
    const copy = copyValue ?? raw;
    return (
        <div className="flex items-start justify-between gap-2 rounded-xl border border-blue-gray-100 bg-white px-3 py-2.5 shadow-sm">
            <div className="flex items-start gap-2 min-w-0 flex-1">
                {RowIcon && <RowIcon size={14} className="text-blue-gray-400 shrink-0 mt-0.5" />}
                <div className="min-w-0 flex-1">
                    <div className="text-[10px] font-semibold text-blue-gray-400 uppercase tracking-wide">{label}</div>
                    {empty ? (
                        <span className="text-sm text-blue-gray-300">—</span>
                    ) : href ? (
                        <a href={href} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-gray-800 font-medium hover:text-accent break-all">
                            {raw}
                        </a>
                    ) : (
                        <span className="text-sm text-blue-gray-800 font-medium break-words">{raw}</span>
                    )}
                </div>
            </div>
            {!empty && copy && (
                <button
                    type="button"
                    title="Copier"
                    onClick={() => { try { navigator.clipboard.writeText(copy); } catch (_) {} }}
                    className="shrink-0 p-1.5 rounded-lg text-blue-gray-400 hover:text-accent hover:bg-blue-gray-50 transition"
                >
                    <Copy size={14} />
                </button>
            )}
        </div>
    );
}

function ScoreDetailCard({ label, value, hint }) {
    const { n, color, bg, border } = scoreCardStyle(value);
    return (
        <div className="rounded-xl border p-3 flex flex-col gap-0.5 text-center" style={{ background: bg, borderColor: border }}>
            <div className="text-lg font-extrabold leading-tight" style={{ color }}>{n}/100</div>
            <div className="text-[11px] font-semibold text-blue-gray-700 leading-tight">{label}</div>
            {hint && <div className="text-[10px] text-blue-gray-400 leading-tight mt-0.5">{hint}</div>}
        </div>
    );
}

/** Même logique que la page audit public : une carte par onglet (Synthèse… IA). */
function ScoreTabCard({ label, value, hint, icon: Icon }) {
    const isEmpty = value === null || value === undefined;
    if (isEmpty) {
        return (
            <div className="rounded-xl border border-blue-gray-100 bg-blue-gray-50/80 p-3 flex flex-col gap-1 text-center min-h-[104px] justify-center">
                {Icon && (
                    <div className="flex justify-center">
                        <Icon size={14} className="text-blue-gray-300" />
                    </div>
                )}
                <div className="text-xl font-extrabold text-blue-gray-300">—</div>
                <div className="text-[11px] font-semibold text-blue-gray-600 leading-tight">{label}</div>
                {hint && <div className="text-[10px] text-blue-gray-400 leading-tight">{hint}</div>}
            </div>
        );
    }
    const { n, color, bg, border } = scoreCardStyle(value);
    return (
        <div className="rounded-xl border p-3 flex flex-col gap-1 text-center min-h-[104px] justify-center" style={{ background: bg, borderColor: border }}>
            {Icon && (
                <div className="flex justify-center">
                    <Icon size={14} style={{ color }} />
                </div>
            )}
            <div className="text-lg font-extrabold leading-tight" style={{ color }}>{n}/100</div>
            <div className="text-[11px] font-semibold text-blue-gray-700 leading-tight">{label}</div>
            {hint && <div className="text-[10px] text-blue-gray-400 leading-tight mt-0.5">{hint}</div>}
        </div>
    );
}

function ProspectDetailPanel({ prospect, onClose, onRefresh, onProspectViewed, userType = 'admin' }) {
    const navigate = useNavigate();
    const { showToast } = useToast();
    const [fullProspect, setFullProspect] = useState(prospect);
    const [loadingDetail, setLoadingDetail] = useState(true);
    const [activeSection, setActiveSection] = useState('info');
    const [stats, setStats] = useState(null);
    const [loadingStats, setLoadingStats] = useState(false);
    const [pitch, setPitch] = useState(null);
    const [loadingPitch, setLoadingPitch] = useState(false);
    const [notes, setNotes] = useState(prospect.lead_notes || '');
    const [savingNotes, setSavingNotes] = useState(false);
    const [emailSubject, setEmailSubject] = useState('');
    const [emailBody, setEmailBody] = useState('');
    const [sendingEmail, setSendingEmail] = useState(false);
    const [converting, setConverting] = useState(false);
    const [convertedSlug, setConvertedSlug] = useState(null);
    /** null = chargement ; conversion bloquée si false (clés Gemini + PageSpeed) */
    const [apiKeysReady, setApiKeysReady] = useState(null);
    const [leadStatus, setLeadStatus] = useState(prospect.lead_status || 'new');
    const [showStatusDropdown, setShowStatusDropdown] = useState(false);
    const [activities, setActivities] = useState([]);
    const [loadingActivities, setLoadingActivities] = useState(false);
    const viewedMarkedRef = React.useRef(false);

    useEffect(() => {
        setApiKeysReady(null);
        getOnboardingStatus()
            .then((s) => setApiKeysReady(!!s?.setup_complete))
            .catch(() => setApiKeysReady(false));
    }, [prospect.token]);

    useEffect(() => {
        setLoadingDetail(true);
        setFullProspect(prospect);
        axiosService.get(`${API_BASE}/api/public/admin/prospects/${prospect.token}`)
            .then(res => {
                setFullProspect(prev => ({ ...prev, ...res.data }));
                if (res.data?.lead_notes != null) setNotes(res.data.lead_notes || '');
                if (res.data?.lead_status) setLeadStatus(res.data.lead_status);
                if (res.data?.converted_project_slug) setConvertedSlug(res.data.converted_project_slug);
            })
            .catch(() => {})
            .finally(() => setLoadingDetail(false));
    }, [prospect.token]);

    const fetchStats = useCallback(async () => {
        setLoadingStats(true);
        try {
            const res = await axiosService.get(`${API_BASE}/api/public/admin/prospects/${prospect.token}/stats`);
            setStats(res.data);
        } catch (e) { console.error(e); }
        finally { setLoadingStats(false); }
    }, [prospect.token]);

    const fetchActivities = useCallback(async () => {
        setLoadingActivities(true);
        try {
            const res = await axiosService.get(`${API_BASE}/api/public/admin/prospects/${prospect.token}/activities`);
            setActivities(res.data?.activities || []);
        } catch (e) { console.error(e); }
        finally { setLoadingActivities(false); }
    }, [prospect.token]);

    useEffect(() => { fetchStats(); }, [fetchStats]);
    useEffect(() => { fetchActivities(); }, [fetchActivities]);

    const returnGapByEventId = useMemo(() => {
        if (!stats?.timeline?.length) return {};
        return buildReturnVisitGapSecondsMap(stats.timeline);
    }, [stats?.timeline]);

    const lastReturnGapSeconds = useMemo(() => {
        if (!stats?.timeline?.length) return null;
        const returns = stats.timeline.filter(ev => ev.event_type === 'return_visit' && ev.timestamp);
        if (!returns.length) return null;
        const last = returns.reduce((a, b) =>
            (!a || new Date(b.timestamp) > new Date(a.timestamp) ? b : a));
        const g = returnGapByEventId[last.id];
        return g != null && !Number.isNaN(g) ? g : null;
    }, [stats?.timeline, returnGapByEventId]);

    // Marquer comme "contacté" quand le staff ouvre la fiche (badge Nouveau → Contacté)
    useEffect(() => {
        if (viewedMarkedRef.current || (prospect.lead_status || 'new') !== 'new') return;
        viewedMarkedRef.current = true;
        axiosService.patch(`${API_BASE}/api/public/admin/prospects/${prospect.token}/lead`, { lead_status: 'in_progress' })
            .then(() => {
                setLeadStatus('in_progress');
                onProspectViewed?.();
            })
            .catch(() => { viewedMarkedRef.current = false; });
    }, [prospect.token, prospect.lead_status, onProspectViewed]);

    const generatePitch = async () => {
        setLoadingPitch(true);
        setPitch(null);
        try {
            const res = await axiosService.get(`${API_BASE}/api/public/admin/prospects/${prospect.token}/commercial-pitch`);
            setPitch(res.data);
            if (res.data.email_subject) setEmailSubject(res.data.email_subject);
            if (res.data.email_body) setEmailBody(res.data.email_body);
            fetchActivities();
        } catch (e) { console.error(e); }
        finally { setLoadingPitch(false); }
    };

    const saveNotes = async () => {
        setSavingNotes(true);
        try {
            await axiosService.patch(`${API_BASE}/api/public/admin/prospects/${prospect.token}/lead`, { lead_notes: notes });
            fetchActivities();
        } catch (e) { console.error(e); }
        finally { setSavingNotes(false); }
    };

    const updateLeadStatus = async (newStatus) => {
        try {
            await axiosService.patch(`${API_BASE}/api/public/admin/prospects/${prospect.token}/lead`, { lead_status: newStatus });
            setLeadStatus(newStatus);
            setShowStatusDropdown(false);
            onRefresh();
            fetchActivities();
        } catch (e) { console.error(e); }
    };

    const sendEmail = async () => {
        if (!emailSubject.trim() || !emailBody.trim()) return;
        setSendingEmail(true);
        try {
            await axiosService.post(`${API_BASE}/api/public/admin/prospects/${prospect.token}/send-email`, {
                subject: emailSubject, body: emailBody
            });
            onRefresh();
            fetchActivities();
        } catch (e) { console.error(e); }
        finally { setSendingEmail(false); }
    };

    const goConfigureApiKeys = () => {
        navigate('/profile', { state: { tab: 'apikeys' } });
        showToast('Configurez les clés Gemini et PageSpeed dans Profil, puis revenez convertir ce prospect.', 'info');
    };

    const convertToProject = async () => {
        if (apiKeysReady === false) {
            goConfigureApiKeys();
            return;
        }
        setConverting(true);
        try {
            const res = await axiosService.post(`${API_BASE}/api/public/admin/prospects/${prospect.token}/convert`);
            const slug = res.data.project_slug;
            setConvertedSlug(slug);
            setFullProspect(prev => ({ ...prev, converted_project_id: res.data.project_id }));
            setLeadStatus('converted');
            onRefresh();
            onClose();
            navigate(`/projects/${slug}/audit`);
        } catch (e) {
            const status = e.response?.status;
            const detail = e.response?.data?.detail;
            const msg = typeof detail === 'string' ? detail : (detail?.message || 'Erreur lors de la conversion');
            if (status === 403 && (typeof msg === 'string' && (msg.includes('Clés API') || msg.includes('PageSpeed') || msg.includes('Gemini')))) {
                goConfigureApiKeys();
            } else {
                showToast(msg, 'error');
            }
        } finally { setConverting(false); }
    };

    const SECTIONS = [
        { key: 'info', label: 'Infos', icon: Users },
        { key: 'stats', label: 'Stats', icon: BarChart3 },
        { key: 'history', label: 'Dossier', icon: ClipboardList },
        { key: 'notes', label: 'Notes internes', icon: StickyNote },
        { key: 'actions', label: 'Actions', icon: Play, soon: true },
        { key: 'pitch', label: 'IA', icon: Sparkles, soon: true },
    ];

    return (
        <div className="fixed inset-0 z-50 flex">
            <div className="absolute inset-0 bg-blue-gray-900/20 backdrop-blur-sm" onClick={onClose} />
            <div className="relative ml-auto w-full max-w-4xl bg-white shadow-xl flex flex-col h-full border-l border-blue-gray-100">
                {/* Header */}
                <div className="flex items-center justify-between p-5 border-b border-blue-gray-100">
                    <div className="flex items-center gap-3 min-w-0">
                        <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
                            <Globe size={20} className="text-accent" />
                        </div>
                        <div className="min-w-0">
                            <div className="font-heading font-semibold text-blue-gray-900 truncate">{fullProspect.url?.replace(/^https?:\/\//, '')}</div>
                            <div className="text-xs text-blue-gray-400">
                                {formatProspectDisplayName(fullProspect)} — {fullProspect.email}
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="relative">
                            <LeadStatusBadge status={leadStatus} onClick={() => setShowStatusDropdown(!showStatusDropdown)} />
                            {showStatusDropdown && (
                                <div className="absolute right-0 top-full mt-1 bg-white rounded-lg shadow-lg border border-blue-gray-200 py-1 z-10 min-w-[140px]">
                                    {LEAD_STATUS_OPTIONS.map(key => {
                                        const val = LEAD_STATUSES[key];
                                        return val ? (
                                            <button
                                                key={key}
                                                onClick={() => updateLeadStatus(key)}
                                                className="w-full text-left px-3 py-1.5 text-sm hover:bg-blue-gray-50 transition flex items-center gap-2"
                                            >
                                                <span className="w-2 h-2 rounded-full" style={{ background: val.text }} />
                                                {val.label}
                                            </button>
                                        ) : null;
                                    })}
                                </div>
                            )}
                        </div>
                        <button onClick={onClose} className="p-2 rounded-lg text-blue-gray-400 hover:text-blue-gray-600 hover:bg-blue-gray-50 transition">
                            <X size={20} />
                        </button>
                    </div>
                </div>

                {fullProspect.status === 'error' && fullProspect.error_message && (
                    <div className="mx-5 mt-0 p-4 rounded-lg bg-red-50 border border-red-200 flex gap-3">
                        <AlertTriangle size={20} className="flex-shrink-0 text-red-500" />
                        <div>
                            <div className="font-semibold text-red-800 text-sm">Audit en erreur</div>
                            <p className="text-sm text-red-700 mt-0.5">{fullProspect.error_message}</p>
                        </div>
                    </div>
                )}

                {/* Section tabs */}
                <div className="flex gap-1 px-5 pt-3 pb-0">
                    {SECTIONS.map(s => {
                        const Icon = s.icon;
                        const active = activeSection === s.key;
                        const isSoon = s.soon === true;
                        return (
                            <button
                                key={s.key}
                                type="button"
                                onClick={() => !isSoon && setActiveSection(s.key)}
                                disabled={isSoon}
                                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                                    isSoon
                                        ? 'text-blue-gray-300 cursor-not-allowed opacity-80'
                                        : active
                                            ? 'bg-accent/10 text-accent'
                                            : 'text-blue-gray-400 hover:text-blue-gray-600 hover:bg-blue-gray-50'
                                }`}
                            >
                                <Icon size={14} className={isSoon ? 'opacity-70' : ''} />
                                {s.label}
                                {isSoon && (
                                    <span className="ml-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-gray-200 text-blue-gray-500">
                                        Bientôt
                                    </span>
                                )}
                            </button>
                        );
                    })}
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-5">
                    {/* ── INFO ── */}
                    {activeSection === 'info' && (
                        <div className="space-y-5">
                            {loadingDetail ? (
                                <div className="text-center py-12 text-blue-gray-400 text-sm">Chargement de la fiche…</div>
                            ) : (
                                <>
                                    {/* Contact — grille (mêmes infos qu’avant) */}
                                    <div>
                                        <h4 className="text-[10px] font-semibold text-blue-gray-400 uppercase tracking-wider mb-2">Contact</h4>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                            <ContactDetailRow label="Prénom" value={fullProspect.first_name} copyValue={fullProspect.first_name} icon={Users} />
                                            <ContactDetailRow label="Nom" value={fullProspect.last_name} copyValue={fullProspect.last_name} icon={Users} />
                                            <ContactDetailRow label="Nom complet (import)" value={fullProspect.name} copyValue={fullProspect.name} icon={Tag} />
                                            <ContactDetailRow label="Entreprise" value={fullProspect.company} copyValue={fullProspect.company} icon={Building2} />
                                            <ContactDetailRow label="E-mail" value={fullProspect.email} href={fullProspect.email ? `mailto:${fullProspect.email}` : null} copyValue={fullProspect.email} icon={Mail} />
                                            <ContactDetailRow label="Téléphone" value={fullProspect.phone} href={fullProspect.phone ? `tel:${fullProspect.phone}` : null} copyValue={fullProspect.phone} icon={Phone} />
                                            <ContactDetailRow
                                                label="Site web"
                                                value={fullProspect.url ? fullProspect.url.replace(/^https?:\/\//, '') : ''}
                                                href={fullProspect.url ? (fullProspect.url.match(/^https?:\/\//) ? fullProspect.url : `https://${fullProspect.url}`) : null}
                                                copyValue={fullProspect.url || ''}
                                                icon={Globe}
                                            />
                                            <ContactDetailRow label="UTM source" value={fullProspect.utm_source} copyValue={fullProspect.utm_source} icon={Tag} />
                                            <ContactDetailRow label="UTM medium" value={fullProspect.utm_medium} copyValue={fullProspect.utm_medium} icon={Tag} />
                                            <ContactDetailRow
                                                label="Origine de la demande"
                                                value={fullProspect.source === 'csv_import' ? 'Import CSV' : fullProspect.source === 'manual' ? 'Lancement manuel' : fullProspect.source === 'landing' ? 'Tunnel landing' : (fullProspect.source || '')}
                                                icon={FileText}
                                            />
                                        </div>
                                    </div>

                                    {/* État du site — secteur + notes globales par onglet (comme page audit public) */}
                                    <div>
                                        <h4 className="text-[10px] font-semibold text-blue-gray-400 uppercase tracking-wider mb-2">État du site</h4>
                                        <div className="rounded-xl border border-accent/20 bg-accent/5 p-4 mb-3">
                                            <div className="text-[10px] font-semibold text-blue-gray-400 uppercase tracking-wide mb-1">Secteur d&apos;activité (IA)</div>
                                            <p className="text-sm font-medium text-blue-gray-800 leading-snug">
                                                {fullProspect.secteur_activite?.trim() ? fullProspect.secteur_activite : '— Non détecté ou analyse en cours'}
                                            </p>
                                        </div>

                                        <p className="text-xs text-blue-gray-500 mb-2">Notes globales par onglet (identiques à l&apos;audit public)</p>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 mb-4">
                                            <ScoreTabCard
                                                label="Synthèse"
                                                value={fullProspect.tab_scores?.synthese ?? fullProspect.score_global}
                                                hint="Note globale (jauge)"
                                                icon={BarChart3}
                                            />
                                            <ScoreTabCard
                                                label="SEO"
                                                value={fullProspect.tab_scores?.seo ?? fullProspect.score_seo}
                                                hint="Référencement page"
                                                icon={Search}
                                            />
                                            <ScoreTabCard
                                                label="Performance"
                                                value={fullProspect.tab_scores?.performance}
                                                hint="Vitesse & Lighthouse"
                                                icon={Gauge}
                                            />
                                            <ScoreTabCard
                                                label="Technique"
                                                value={fullProspect.tab_scores?.technique}
                                                hint="HTTPS, mobile, index…"
                                                icon={Wrench}
                                            />
                                        </div>

                                        {((fullProspect.tab_scores?.synthese ?? fullProspect.score_global) || 0) < 60 && (
                                            <p className="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                                                Site avec des axes d&apos;amélioration importants — opportunité commerciale à saisir.
                                            </p>
                                        )}
                                    </div>

                                    {/* Actions */}
                                    <div className="flex flex-wrap gap-2 pt-1">
                                        <a
                                            href={`/audit/${prospect.token}${STAFF_AUDIT_QUERY}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            onClick={async () => {
                                                if ((leadStatus || 'new') === 'new') {
                                                    try {
                                                        await axiosService.patch(`${API_BASE}/api/public/admin/prospects/${prospect.token}/lead`, { lead_status: 'in_progress' });
                                                        setLeadStatus('in_progress');
                                                        onProspectViewed?.();
                                                    } catch (_) {}
                                                }
                                            }}
                                            className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-accent rounded-lg hover:bg-accent-dark transition"
                                        >
                                            <Eye size={14} />
                                            Voir l&apos;audit
                                        </a>
                                        <a
                                            href={(fullProspect.url?.match(/^https?:\/\//) ? fullProspect.url : `https://${fullProspect.url || ''}`)}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-blue-gray-700 bg-white border border-blue-gray-200 rounded-lg hover:bg-blue-gray-50 transition"
                                        >
                                            <Globe size={14} />
                                            Ouvrir le site
                                        </a>
                                        {!fullProspect.converted_project_id && (
                                            <div className="flex flex-col gap-1">
                                                <button
                                                    type="button"
                                                    onClick={convertToProject}
                                                    disabled={converting || apiKeysReady === false || apiKeysReady === null}
                                                    className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-accent bg-accent/10 rounded-lg hover:bg-accent/15 transition disabled:opacity-50"
                                                    title={apiKeysReady === false ? 'Configurez d’abord Gemini et PageSpeed dans Profil > Clés API' : undefined}
                                                >
                                                    {converting ? <Loader2 size={14} className="animate-spin" /> : <ArrowRightLeft size={14} />}
                                                    {converting ? 'Conversion…' : apiKeysReady === null ? 'Vérification…' : 'Convertir en audit complet'}
                                                </button>
                                                {apiKeysReady === false && (
                                                    <button
                                                        type="button"
                                                        onClick={goConfigureApiKeys}
                                                        className="text-left text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 hover:bg-amber-100/80 transition"
                                                    >
                                                        Clés API manquantes : ouvrir Profil → Clés API (Gemini + PageSpeed requis).
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                        {fullProspect.converted_project_id && (
                                            <div className="flex flex-col gap-2">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <span className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-green-700 bg-green-50 rounded-lg border border-green-200">
                                                        <CheckCircle size={14} />
                                                        Converti
                                                    </span>
                                                    {convertedSlug && (
                                                        <Link
                                                            to={`/projects/${convertedSlug}`}
                                                            className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-accent rounded-lg hover:bg-accent-dark transition"
                                                        >
                                                            <FolderOpen size={14} />
                                                            Voir l&apos;audit complet
                                                        </Link>
                                                    )}
                                                </div>
                                                {!convertedSlug && !loadingDetail && (
                                                    <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                                                        Le projet lié est introuvable (supprimé ou base non à jour). Exécutez les migrations Alembic ou contactez un administrateur.
                                                    </p>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>
                    )}

                    {/* ── NOTES INTERNES ── */}
                    {activeSection === 'notes' && (
                        <div className="space-y-4">
                            <h3 className="text-sm font-heading font-semibold text-blue-gray-700">Notes internes</h3>
                            <p className="text-xs text-blue-gray-500">Notes privées sur ce prospect, visibles uniquement par votre équipe.</p>
                            <textarea
                                value={notes}
                                onChange={e => setNotes(e.target.value)}
                                className="w-full p-3 text-sm border border-blue-gray-200 rounded-lg outline-none focus:ring-2 focus:ring-accent/20 focus:border-accent/40 resize-y min-h-[160px] bg-white"
                                placeholder="Notes sur ce prospect..."
                            />
                            <button
                                onClick={saveNotes}
                                disabled={savingNotes}
                                className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-accent bg-accent/10 rounded-lg hover:bg-accent/15 transition disabled:opacity-50"
                            >
                                {savingNotes ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                                {savingNotes ? 'Enregistrement...' : 'Enregistrer les notes'}
                            </button>
                        </div>
                    )}

                    {/* ── HISTORIQUE DOSSIER ── */}
                    {activeSection === 'history' && (
                        <div className="space-y-4">
                            <div className="flex items-center justify-between">
                                <h3 className="text-sm font-heading font-semibold text-blue-gray-700">Historique du dossier</h3>
                                <button
                                    onClick={fetchActivities}
                                    className="flex items-center gap-1.5 text-xs text-accent hover:text-accent-dark transition"
                                >
                                    <RefreshCw size={12} className={loadingActivities ? 'animate-spin' : ''} />
                                    Actualiser
                                </button>
                            </div>

                            {loadingActivities ? (
                                <div className="text-center py-12 text-blue-gray-400">Chargement de l'historique...</div>
                            ) : activities.length === 0 ? (
                                <div className="text-center py-12 text-blue-gray-300 text-sm">Aucune activité enregistrée.</div>
                            ) : (
                                <div className="relative">
                                    <div className="absolute left-4 top-2 bottom-2 w-px bg-blue-gray-100" />
                                    <div className="space-y-0">
                                        {activities.map((act, i) => {
                                            const ACTIVITY_ICONS = {
                                                audit_requested: { icon: FileText, color: 'text-accent', bg: 'bg-accent/10' },
                                                email_lead_notification_sent: { icon: Mailbox, color: 'text-info', bg: 'bg-info/10' },
                                                audit_completed: { icon: CheckCircle, color: 'text-success', bg: 'bg-success/10' },
                                                email_prospect_sent: { icon: MailCheck, color: 'text-success', bg: 'bg-success/10' },
                                                status_change: { icon: ArrowRightLeft, color: 'text-warning', bg: 'bg-warning/10' },
                                                note_added: { icon: StickyNote, color: 'text-blue-gray-500', bg: 'bg-blue-gray-50' },
                                                email_sent: { icon: Send, color: 'text-info', bg: 'bg-info/10' },
                                                pitch_generated: { icon: BrainCircuit, color: 'text-secondary', bg: 'bg-secondary/10' },
                                                converted: { icon: UserCheck, color: 'text-success', bg: 'bg-success/10' },
                                            };
                                            const cfg = ACTIVITY_ICONS[act.activity_type] || { icon: Activity, color: 'text-blue-gray-400', bg: 'bg-blue-gray-50' };
                                            const Icon = cfg.icon;
                                            const dateStr = act.created_at ? new Date(act.created_at).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';
                                            const isEmailToProspect = act.activity_type === 'email_prospect_sent';
                                            const isLeadNotif = act.activity_type === 'email_lead_notification_sent';

                                            return (
                                                <div key={act.id || i} className="relative pl-10 pr-2 py-3 group">
                                                    <div className={`absolute left-2 top-4 w-5 h-5 rounded-full ${cfg.bg} flex items-center justify-center ring-2 ring-white`}>
                                                        <Icon size={11} className={cfg.color} />
                                                    </div>
                                                    <div className="flex items-start justify-between gap-3">
                                                        <div className="flex-1 min-w-0">
                                                            <p className="text-sm font-medium text-blue-gray-700">{act.title}</p>
                                                            {act.detail && !isEmailToProspect && !isLeadNotif && (
                                                                <p className="text-xs text-blue-gray-400 mt-1 line-clamp-3 whitespace-pre-line">{act.detail}</p>
                                                            )}
                                                            {isLeadNotif && act.detail && (
                                                                <p className="text-xs text-blue-gray-400 mt-1">Destinataires : {act.detail}</p>
                                                            )}
                                                            {isEmailToProspect && act.detail && (
                                                                <p className="text-xs text-blue-gray-400 mt-1">{act.detail}</p>
                                                            )}
                                                            {act.activity_type === 'pitch_generated' && act.detail && (() => {
                                                                try {
                                                                    const parsed = JSON.parse(act.detail);
                                                                    return (
                                                                        <div className="mt-2 space-y-2">
                                                                            {parsed.summary && <p className="text-xs text-blue-gray-500 bg-blue-gray-50 rounded-lg p-2"><strong>Résumé :</strong> {parsed.summary}</p>}
                                                                            {parsed.email_subject && <p className="text-xs text-blue-gray-500"><strong>Objet email :</strong> {parsed.email_subject}</p>}
                                                                        </div>
                                                                    );
                                                                } catch { return null; }
                                                            })()}
                                                        </div>
                                                        <span className="text-[10px] text-blue-gray-300 whitespace-nowrap mt-0.5">{dateStr}</span>
                                                    </div>
                                                    {i < activities.length - 1 && <div className="h-px bg-blue-gray-50 mt-3" />}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* ── STATS ── */}
                    {activeSection === 'stats' && (
                        <div className="space-y-5">
                            {loadingStats ? (
                                <div className="text-center py-12 text-blue-gray-400">Chargement des stats...</div>
                            ) : !stats ? (
                                <div className="text-center py-12 text-blue-gray-400">Aucune donnée de visite</div>
                            ) : (
                                <>
                                    {/* Résumé comportemental */}
                                    <div className="grid grid-cols-2 gap-3">
                                        <div className="rounded-xl border border-blue-gray-100 bg-white p-4 flex flex-col gap-1">
                                            <div className="flex items-center gap-1.5 text-blue-gray-400 text-[10px] font-semibold uppercase tracking-wider mb-1">
                                                <Eye size={11} />
                                                Visites
                                            </div>
                                            <div className="text-2xl font-extrabold text-blue-gray-900">{stats.unique_sessions ?? 0}</div>
                                            <div className="text-[11px] text-blue-gray-400">session{(stats.unique_sessions ?? 0) > 1 ? 's' : ''} sur l'audit</div>
                                        </div>
                                        <div className={`rounded-xl border-2 p-4 flex flex-col gap-1 ${stats.is_return_visitor ? 'border-amber-200 bg-amber-50' : 'border-blue-gray-100 bg-white'}`}>
                                            <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider mb-1" style={{ color: stats.is_return_visitor ? '#b45309' : '#94a3b8' }}>
                                                <RotateCcw size={11} />
                                                Retours
                                            </div>
                                            <div className="text-2xl font-extrabold" style={{ color: stats.is_return_visitor ? '#b45309' : '#1e293b' }}>{stats.return_visits ?? 0}</div>
                                            <div className="text-[11px]" style={{ color: stats.is_return_visitor ? '#92400e' : '#94a3b8' }}>
                                                {stats.is_return_visitor ? 'Est revenu consulter l\'audit' : 'Pas de visite retour'}
                                            </div>
                                            {stats.is_return_visitor && lastReturnGapSeconds != null && (
                                                <div className="text-[10px] font-medium mt-1 leading-snug" style={{ color: '#b45309' }}>
                                                    Dernier retour : {formatDuration(lastReturnGapSeconds)} après la session précédente
                                                </div>
                                            )}
                                        </div>
                                        <div className="rounded-xl border border-blue-gray-100 bg-white p-4 flex flex-col gap-1">
                                            <div className="flex items-center gap-1.5 text-blue-gray-400 text-[10px] font-semibold uppercase tracking-wider mb-1">
                                                <Timer size={11} />
                                                Temps total passé
                                            </div>
                                            <div className="text-2xl font-extrabold text-blue-gray-900">{formatDuration(stats.total_session_duration)}</div>
                                            <div className="text-[11px] text-blue-gray-400">sur l'ensemble des visites</div>
                                        </div>
                                        <div className="rounded-xl border border-blue-gray-100 bg-white p-4 flex flex-col gap-1">
                                            <div className="flex items-center gap-1.5 text-blue-gray-400 text-[10px] font-semibold uppercase tracking-wider mb-1">
                                                <Timer size={11} />
                                                Durée moy. / visite
                                            </div>
                                            <div className="text-2xl font-extrabold text-blue-gray-900">{stats.avg_session_duration ? formatDuration(stats.avg_session_duration) : '–'}</div>
                                            <div className="text-[11px] text-blue-gray-400">par session</div>
                                        </div>
                                    </div>

                                    {/* A-t-il demandé à être contacté ? */}
                                    {stats.cta_clicks && stats.cta_clicks.length > 0 ? (
                                        <div className="rounded-xl border-2 border-emerald-200 bg-emerald-50 p-4">
                                            <div className="flex items-center gap-2 mb-2">
                                                <Phone size={14} className="text-emerald-600" />
                                                <span className="text-sm font-semibold text-emerald-800">A demandé à être contacté</span>
                                            </div>
                                            <div className="flex flex-wrap gap-1.5">
                                                {stats.cta_clicks.map((c, i) => (
                                                    <span key={i} className="px-2.5 py-1 bg-emerald-100 text-emerald-700 text-xs rounded-full font-medium border border-emerald-200">
                                                        {getEventLabelDisplay(c)}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="rounded-xl border border-blue-gray-100 bg-blue-gray-50 p-4 flex items-center gap-2">
                                            <Phone size={14} className="text-blue-gray-300" />
                                            <span className="text-sm text-blue-gray-400">N'a pas encore cliqué sur un bouton de contact</span>
                                        </div>
                                    )}

                                    {/* Ce qui l'intéresse */}
                                    {stats.tab_clicks && Object.keys(stats.tab_clicks).length > 0 && (
                                        <div className="rounded-xl border border-blue-gray-100 bg-white p-4">
                                            <h4 className="text-xs font-semibold text-blue-gray-700 mb-3 flex items-center gap-1.5">
                                                <Layers size={13} className="text-accent" />
                                                Ce qui l'intéresse le plus
                                            </h4>
                                            <div className="space-y-2.5">
                                                {Object.entries(stats.tab_clicks).sort((a, b) => b[1] - a[1]).map(([label, count]) => {
                                                    const max = Math.max(...Object.values(stats.tab_clicks));
                                                    const tabName = TAB_SHORT_LABELS[label] || label.replace('tab_', '').replace('_', ' ');
                                                    return (
                                                        <div key={label} className="flex items-center gap-3">
                                                            <span className="text-xs text-blue-gray-600 w-20 truncate font-medium">{tabName}</span>
                                                            <div className="flex-1 h-2 bg-blue-gray-100 rounded-full overflow-hidden">
                                                                <div className="h-full bg-accent rounded-full transition-all" style={{ width: `${(count / max) * 100}%` }} />
                                                            </div>
                                                            <span className="text-xs font-bold text-blue-gray-700 w-5 text-right">{count}</span>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}

                                    {/* Timeline simplifiée */}
                                    {stats.timeline && stats.timeline.length > 0 && (
                                        <div>
                                            <h4 className="text-xs font-semibold text-blue-gray-500 mb-2">Historique des visites</h4>
                                            <div className="space-y-1 max-h-[260px] overflow-y-auto rounded-xl border border-blue-gray-100 bg-white divide-y divide-blue-gray-50">
                                                {stats.timeline.slice().reverse().filter(ev =>
                                                    !(ev.event_label || '').match(/^scroll_\d+$/) &&
                                                    !['page_view', 'session_end'].includes(ev.event_type)
                                                ).map(ev => (
                                                    <div key={ev.id} className="flex items-center gap-3 py-2 px-3 text-xs">
                                                        <EventIcon type={ev.event_type} />
                                                        <span className="text-blue-gray-700 flex-1 min-w-0">{getEventLabelDisplay(ev.event_label)}</span>
                                                        {ev.event_type === 'return_visit' && returnGapByEventId[ev.id] != null ? (
                                                            <span className="text-amber-700/90 text-[11px] shrink-0" title="Temps écoulé depuis la fin de la session précédente">
                                                                Après {formatDuration(returnGapByEventId[ev.id])}
                                                            </span>
                                                        ) : ev.duration ? (
                                                            <span className="text-blue-gray-400 text-[11px] shrink-0">{formatDuration(ev.duration)}</span>
                                                        ) : null}
                                                        <span className="text-blue-gray-400 text-[11px] shrink-0 text-right">
                                                            {formatProspectEventDateTime(ev.timestamp)}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    )}

                    {/* ── ACTIONS ── */}
                    {activeSection === 'actions' && (
                        <div className="space-y-5">
                            <div>
                                <h4 className="text-[10px] font-heading font-semibold text-blue-gray-500 uppercase tracking-wider mb-3">Pipeline de conversion</h4>
                                <div className="flex items-center gap-1 flex-wrap">
                                    {LEAD_STATUS_OPTIONS.map((key, idx) => {
                                        const val = LEAD_STATUSES[key];
                                        const active = key === leadStatus || (key === 'in_progress' && leadStatus === 'contacted');
                                        return val ? (
                                            <React.Fragment key={key}>
                                                <button
                                                    onClick={() => updateLeadStatus(key)}
                                                    className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all border"
                                                    style={active
                                                        ? { background: val.bg, color: val.text, borderColor: val.text + '30' }
                                                        : { background: '#f8fafc', color: '#94a3b8', borderColor: '#e2e8f0' }
                                                    }
                                                >
                                                    {active && <CheckCircle size={12} />}
                                                    {val.label}
                                                </button>
                                                {idx < LEAD_STATUS_OPTIONS.length - 1 && (
                                                    <ChevronRight size={14} className="text-blue-gray-200 flex-shrink-0" />
                                                )}
                                            </React.Fragment>
                                        ) : null;
                                    })}
                                </div>
                            </div>

                            <div className="border border-blue-gray-100 rounded-lg p-4 bg-white/60">
                                <h4 className="text-[10px] font-heading font-semibold text-blue-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                                    <Mail size={14} className="text-blue-gray-400" />
                                    Envoyer un email
                                </h4>
                                <input
                                    className="w-full px-3 py-2 text-sm border border-blue-gray-200 rounded-lg mb-2 outline-none focus:ring-2 focus:ring-accent/20 focus:border-accent/40 bg-white"
                                    placeholder="Objet de l'email"
                                    value={emailSubject}
                                    onChange={e => setEmailSubject(e.target.value)}
                                />
                                <textarea
                                    className="w-full px-3 py-2 text-sm border border-blue-gray-200 rounded-lg outline-none focus:ring-2 focus:ring-accent/20 focus:border-accent/40 resize-y min-h-[100px] bg-white"
                                    placeholder="Corps de l'email..."
                                    value={emailBody}
                                    onChange={e => setEmailBody(e.target.value)}
                                />
                                <button
                                    onClick={sendEmail}
                                    disabled={sendingEmail || !emailSubject.trim() || !emailBody.trim()}
                                    className="mt-2 flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-accent rounded-lg hover:bg-accent-dark transition disabled:opacity-50"
                                >
                                    {sendingEmail ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                                    Envoyer
                                </button>
                            </div>
                        </div>
                    )}

                    {/* ── IA PITCH ── */}
                    {activeSection === 'pitch' && (
                        <div className="space-y-4">
                            {!pitch && !loadingPitch && (
                                <button
                                    onClick={generatePitch}
                                    className="w-full flex items-center justify-center gap-2 px-4 py-4 text-sm font-medium text-accent bg-accent/8 rounded-lg hover:bg-accent/12 transition border border-accent/15"
                                >
                                    <Sparkles size={18} />
                                    Générer le discours commercial (Gemini)
                                </button>
                            )}
                            {loadingPitch && (
                                <div className="text-center py-12">
                                    <Loader2 size={28} className="text-accent animate-spin mx-auto mb-3" />
                                    <p className="text-sm text-blue-gray-400">Gemini analyse le profil du prospect...</p>
                                </div>
                            )}
                            {pitch && (
                                <div className="space-y-4">
                                    {pitch.summary && (
                                        <div className="bg-accent/5 rounded-lg p-4 border border-accent/10">
                                            <h4 className="text-[10px] font-heading font-semibold text-accent uppercase tracking-wider mb-2">Résumé</h4>
                                            <p className="text-sm text-blue-gray-700 leading-relaxed">{pitch.summary}</p>
                                        </div>
                                    )}
                                    {pitch.pitch && (
                                        <div className="bg-white/60 rounded-lg p-4 border border-blue-gray-100">
                                            <h4 className="text-[10px] font-heading font-semibold text-blue-gray-500 uppercase tracking-wider mb-2">Discours commercial</h4>
                                            <p className="text-sm text-blue-gray-700 leading-relaxed whitespace-pre-line">{pitch.pitch}</p>
                                        </div>
                                    )}
                                    {pitch.talking_points && (
                                        <div className="bg-amber-50/60 rounded-lg p-4 border border-amber-200/40">
                                            <h4 className="text-[10px] font-heading font-semibold text-amber-700 uppercase tracking-wider mb-2">Points clés pour l'appel</h4>
                                            <ul className="space-y-1.5">
                                                {pitch.talking_points.map((p, i) => (
                                                    <li key={i} className="flex items-start gap-2 text-sm text-blue-gray-700">
                                                        <span className="text-amber-500 font-bold mt-0.5">{i + 1}.</span>
                                                        {p}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                    {pitch.email_subject && (
                                        <div className="bg-blue-gray-50 rounded-lg p-4 border border-blue-gray-100">
                                            <h4 className="text-[10px] font-heading font-semibold text-blue-gray-500 uppercase tracking-wider mb-2">Email suggéré</h4>
                                            <div className="text-xs text-blue-gray-400 mb-1">Objet : <span className="text-blue-gray-700 font-medium">{pitch.email_subject}</span></div>
                                            <p className="text-sm text-blue-gray-600 whitespace-pre-line mt-2">{pitch.email_body}</p>
                                            <button
                                                onClick={() => { setEmailSubject(pitch.email_subject); setEmailBody(pitch.email_body); setActiveSection('actions'); }}
                                                className="mt-3 flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-accent bg-accent/10 rounded-lg hover:bg-accent/15 transition"
                                            >
                                                <Send size={12} />
                                                Utiliser cet email
                                            </button>
                                        </div>
                                    )}
                                    <button
                                        onClick={generatePitch}
                                        className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-blue-gray-400 bg-blue-gray-50 rounded-lg hover:bg-blue-gray-100 transition"
                                    >
                                        <RefreshCw size={12} />
                                        Regénérer
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function InfoField({ icon: Icon, label, value }) {
    return (
        <div className="flex items-center gap-2 bg-blue-gray-50 rounded-lg p-3">
            <Icon size={14} className="text-blue-gray-400 flex-shrink-0" />
            <div className="min-w-0">
                <div className="text-[10px] text-blue-gray-400 uppercase tracking-wider">{label}</div>
                <div className="text-sm text-blue-gray-700 font-medium truncate">{value || <span className="text-blue-gray-300 italic">–</span>}</div>
            </div>
        </div>
    );
}

function MiniStat({ label, value, color, icon: CustomIcon }) {
    return (
        <div className="bg-white/60 rounded-lg p-3 border border-blue-gray-100">
            <div className="text-[10px] text-blue-gray-400 uppercase tracking-wider mb-1 flex items-center gap-1">
                {CustomIcon && <CustomIcon size={10} />}
                {label}
            </div>
            <div className="text-lg font-extrabold" style={{ color }}>{value}</div>
        </div>
    );
}

function EventIcon({ type }) {
    const map = {
        page_view: <Eye size={12} className="text-blue-gray-400" />,
        tab_click: <Layers size={12} className="text-accent" />,
        cta_click: <ArrowUpRight size={12} className="text-green-500" />,
        session_end: <Timer size={12} className="text-amber-500" />,
        scroll: <ChevronDown size={12} className="text-blue-gray-300" />,
        return_visit: <RotateCcw size={12} className="text-amber-500" />,
        form_submit: <FileText size={12} className="text-blue-gray-400" />,
    };
    return map[type] || <Activity size={12} className="text-blue-gray-300" />;
}

// ── Onglet Demandes ──

// Libellés pour l'onglet le plus vu (event_label → affichage)
const TOP_TAB_LABELS = {
    tab_synthese: 'Synthèse',
    tab_seo: 'SEO',
    tab_performance: 'Performance',
    tab_technique: 'Technique',
    tab_marche: 'Marché',
    tab_ia: 'Analyse IA',
    tab_errors: 'Erreurs',
};

const EXPORT_EXTRA_COLUMNS = [
    { key: 'statut_lead', label: 'Statut lead' },
    { key: 'score_global', label: 'Score global' },
    { key: 'score_seo_titre', label: 'Score SEO titre' },
    { key: 'score_meta', label: 'Score meta' },
    { key: 'score_h1', label: 'Score H1' },
    { key: 'score_images', label: 'Score images' },
    { key: 'perf_desktop', label: 'Performance desktop' },
    { key: 'perf_mobile', label: 'Performance mobile' },
    { key: 'accessibilite', label: 'Accessibilite' },
    { key: 'bonnes_pratiques', label: 'Bonnes pratiques' },
    { key: 'score_seo_pagespeed', label: 'Score SEO PageSpeed' },
    { key: 'nb_erreurs', label: 'Nb erreurs' },
    { key: 'nb_alertes', label: 'Nb alertes' },
    { key: 'nb_pages_sitemap', label: 'Nb pages sitemap' },
    { key: 'cms', label: 'CMS' },
    { key: 'technologie', label: 'Technologie' },
    { key: 'hebergeur', label: 'Hebergeur' },
    { key: 'https', label: 'HTTPS' },
    { key: 'secteur_activite', label: "Secteur d'activite (IA)" },
    { key: 'date_creation', label: 'Date creation' },
    { key: 'statut_audit', label: 'Statut audit' },
    { key: 'lien_audit', label: 'Lien audit' },
    { key: 'favicon_url', label: 'Favicon URL' },
];
const EXPORT_EXTRAS_STORAGE_KEY = 'prospects_export_extra_columns_v1';

function DemandesTab({
    onSelectProspect,
    refetchKey = 0,
    onListRefresh,
    selectedToken = null,
    prospectListId = null,   // number → filtre par liste CSV ; null → tunnel uniquement
    tunnelOnly = false,      // true = n'affiche que source=landing
    onOpenCsvImport,         // callback pour ouvrir le modal depuis la barre d'outils
}) {
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const PAGE_SIZE = 30;
    const [page, setPage] = useState(1);
    const [searchInput, setSearchInput] = useState('');
    const [searchDebounced, setSearchDebounced] = useState('');
    const [filterStatus, setFilterStatus] = useState('');
    const [loading, setLoading] = useState(true);
    const [sortKey, setSortKey] = useState('created_at');
    const [sortDir, setSortDir] = useState('desc');
    const [prospectToDelete, setProspectToDelete] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);

    // ── CSV Export / Batch ──
    const [exportLoading, setExportLoading] = useState(false);
    const [exportModalOpen, setExportModalOpen] = useState(false);
    const [selectedExportExtras, setSelectedExportExtras] = useState(() => {
        try {
            const raw = localStorage.getItem(EXPORT_EXTRAS_STORAGE_KEY);
            if (!raw) return new Set(EXPORT_EXTRA_COLUMNS.map(c => c.key));
            const parsed = JSON.parse(raw);
            if (!Array.isArray(parsed)) return new Set(EXPORT_EXTRA_COLUMNS.map(c => c.key));
            const allowed = new Set(EXPORT_EXTRA_COLUMNS.map(c => c.key));
            const restored = parsed.filter(k => allowed.has(k));
            return new Set(restored);
        } catch {
            return new Set(EXPORT_EXTRA_COLUMNS.map(c => c.key));
        }
    });
    const [selectedTokens, setSelectedTokens] = useState(new Set());
    const [batchLaunching, setBatchLaunching] = useState(false);
    const [batchResult, setBatchResult] = useState(null);

    const fetchProspects = useCallback(async ({ silent = false } = {}) => {
        if (!silent) setLoading(true);
        try {
            const params = { page, limit: PAGE_SIZE, sort: sortKey, order: sortDir };
            if (filterStatus) params.lead_status = filterStatus;
            if (prospectListId != null) params.prospect_list_id = prospectListId;
            else if (tunnelOnly) params.tunnel_only = true;
            const q = searchDebounced.trim();
            if (q) params.q = q;
            const res = await axiosService.get(`${API_BASE}/api/public/admin/prospects`, { params });
            setItems(res.data.items || []);
            setTotal(res.data.total || 0);
        } catch (e) { console.error(e); }
        finally { if (!silent) setLoading(false); }
    }, [page, filterStatus, prospectListId, tunnelOnly, searchDebounced, sortKey, sortDir]);

    useEffect(() => {
        const t = setTimeout(() => setSearchDebounced(searchInput), 400);
        return () => clearTimeout(t);
    }, [searchInput]);

    useLayoutEffect(() => {
        setPage(1);
    }, [searchDebounced, sortKey, sortDir, prospectListId, tunnelOnly, filterStatus]);

    useEffect(() => { fetchProspects(); }, [fetchProspects, refetchKey]);

    useEffect(() => {
        try {
            localStorage.setItem(EXPORT_EXTRAS_STORAGE_KEY, JSON.stringify(Array.from(selectedExportExtras)));
        } catch (_) {}
    }, [selectedExportExtras]);

    // Rafraîchissement automatique tant que des audits sont en cours (temps réel).
    useEffect(() => {
        const hasRunningAudits = items.some(i => ['processing', 'pending'].includes(i.status));
        if (!hasRunningAudits) return;
        const intervalId = setInterval(() => {
            fetchProspects({ silent: true });
        }, 4000);
        return () => clearInterval(intervalId);
    }, [items, fetchProspects]);

    // Désélectionner tout quand la page, filtres, recherche ou tri changent
    useEffect(() => { setSelectedTokens(new Set()); }, [page, filterStatus, refetchKey, searchDebounced, sortKey, sortDir, prospectListId, tunnelOnly]);

    // ── Export CSV ──
    const handleExportCsv = useCallback(async (forcedExtras = null) => {
        setExportLoading(true);
        try {
            const params = new URLSearchParams();
            if (filterStatus) params.set('lead_status', filterStatus);
            if (prospectListId != null) params.set('prospect_list_id', prospectListId);
            else if (tunnelOnly) params.set('tunnel_only', 'true');
            const extras = Array.isArray(forcedExtras)
                ? forcedExtras
                : Array.from(selectedExportExtras);
            params.set('extra_columns', extras.join(','));
            const url = `${API_BASE}/api/public/admin/prospects/export-csv${params.toString() ? '?' + params.toString() : ''}`;
            const res = await axiosService.get(url, { responseType: 'blob' });
            const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            const now = new Date();
            link.download = `prospects_${now.getFullYear()}${String(now.getMonth()+1).padStart(2,'0')}${String(now.getDate()).padStart(2,'0')}.csv`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(link.href);
        } catch (e) {
            console.error('Export CSV error:', e);
            alert('Erreur lors de l\'export CSV.');
        } finally {
            setExportLoading(false);
        }
    }, [filterStatus, prospectListId, tunnelOnly, selectedExportExtras]);

    // ── Sélection ──
    const toggleSelect = useCallback((token, e) => {
        e.stopPropagation();
        setSelectedTokens(prev => {
            const next = new Set(prev);
            if (next.has(token)) next.delete(token);
            else next.add(token);
            return next;
        });
    }, []);

    const toggleSelectAll = useCallback((visibleItems) => {
        setSelectedTokens(prev => {
            const allSelected = visibleItems.every(i => prev.has(i.token));
            if (allSelected) return new Set();
            return new Set(visibleItems.map(i => i.token));
        });
    }, []);

    // ── Batch audit sur sélection ──
    const handleBatchAuditSelection = useCallback(async (tokensSet, allItems) => {
        if (tokensSet.size === 0) return;
        setBatchLaunching(true);
        setBatchResult(null);
        try {
            const selectedItems = allItems.filter(i => tokensSet.has(i.token));
            const prospects = selectedItems.map(i => ({
                token: i.token,
                url: i.url,
                email: i.email && !i.email.endsWith('@import.local') ? i.email : '',
                first_name: i.first_name || '',
                last_name: i.last_name || i.name || '',
                phone: i.phone || '',
                company: i.company || '',
            }));
            const res = await axiosService.post(`${API_BASE}/api/public/admin/prospects/batch-audit`, {
                prospects,
                run_audit: true,
                source: 'manual',
                dedup_days: 0,
                prospect_list_id: prospectListId ?? undefined,
            });
            setBatchResult(res.data);
            setSelectedTokens(new Set());
            if (onListRefresh) onListRefresh();
            setTimeout(fetchProspects, 1000);
        } catch (e) {
            setBatchResult({ error: e.response?.data?.detail || e.message });
        } finally {
            setBatchLaunching(false);
        }
    }, [fetchProspects, onListRefresh]);

    const handleSort = (key) => {
        if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        else { setSortKey(key); setSortDir('desc'); }
    };

    const Th = ({ colKey, children, className = '' }) => (
        <th className={`px-4 py-3 text-left text-[10px] font-heading font-semibold text-blue-gray-500 uppercase tracking-wider whitespace-nowrap ${className}`}>
            <button type="button" onClick={() => handleSort(colKey)} className="flex items-center gap-1 hover:text-blue-gray-700 transition">
                {children}
                {sortKey === colKey && (sortDir === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />)}
            </button>
        </th>
    );

    return (
        <div className="flex flex-col gap-4">
            {/* ── Barre d'outils principale ── */}
            <div className="flex items-center gap-3 flex-wrap">
                <div className="relative flex-1 max-w-sm">
                    <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-blue-gray-300" />
                    <input
                        className="w-full pl-9 pr-4 py-2 text-sm border border-blue-gray-200 rounded-lg outline-none focus:ring-2 focus:ring-accent/20 focus:border-accent/40 bg-white"
                        placeholder="Rechercher URL, email, nom, tél, entreprise…"
                        value={searchInput}
                        onChange={e => setSearchInput(e.target.value)}
                    />
                </div>
                <select
                    className="px-3 py-2 text-sm border border-blue-gray-200 rounded-lg bg-white outline-none focus:ring-2 focus:ring-accent/20 focus:border-accent/40 text-blue-gray-600"
                    value={filterStatus}
                    onChange={e => { setFilterStatus(e.target.value); }}
                >
                    <option value="">Tous les statuts</option>
                    {LEAD_STATUS_OPTIONS.map(key => {
                        const val = LEAD_STATUSES[key];
                        return val ? <option key={key} value={key}>{val.label}</option> : null;
                    })}
                </select>
                <button
                    onClick={() => fetchProspects()}
                    disabled={loading}
                    className="flex items-center gap-2 px-3 py-2 text-sm text-blue-gray-600 bg-white border border-blue-gray-200 rounded-lg hover:bg-blue-gray-50 transition disabled:opacity-60 disabled:cursor-wait"
                >
                    {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                    Actualiser
                </button>
                {/* Le bouton Importer CSV est géré par la barre de sous-onglets du parent */}
                {onOpenCsvImport && (
                    <button
                        onClick={onOpenCsvImport}
                        className="flex items-center gap-2 px-3 py-2 text-sm text-white bg-accent hover:bg-accent/90 border border-accent rounded-lg transition font-medium"
                        title="Importer une liste de prospects depuis un fichier CSV"
                    >
                        <Upload size={14} />
                        Importer CSV
                    </button>
                )}
                <button
                    onClick={() => setExportModalOpen(true)}
                    disabled={exportLoading}
                    className="flex items-center gap-2 px-3 py-2 text-sm text-blue-gray-600 bg-white border border-blue-gray-200 rounded-lg hover:bg-blue-gray-50 transition disabled:opacity-60 disabled:cursor-wait"
                    title="Exporter la liste filtrée en CSV"
                >
                    {exportLoading ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
                    Exporter CSV
                </button>
                <span className="text-sm text-blue-gray-400">{total} prospect{total > 1 ? 's' : ''}</span>
            </div>

            {/* ── Barre d'actions contextuelle (sélection active — uniquement sur listes CSV) ── */}
            {!tunnelOnly && selectedTokens.size > 0 && (
                <div className="flex items-center gap-3 px-4 py-2.5 bg-accent/10 border border-accent/30 rounded-xl flex-wrap">
                    <span className="text-sm font-medium text-accent">{selectedTokens.size} sélectionné{selectedTokens.size > 1 ? 's' : ''}</span>
                    <button
                        onClick={() => handleBatchAuditSelection(selectedTokens, items)}
                        disabled={batchLaunching}
                        className="flex items-center gap-2 px-3 py-1.5 text-sm text-white bg-accent hover:bg-accent/90 rounded-lg transition disabled:opacity-60 font-medium"
                    >
                        {batchLaunching ? <Loader2 size={13} className="animate-spin" /> : <PlayCircle size={13} />}
                        Lancer les audits
                    </button>
                    <button
                        onClick={() => setSelectedTokens(new Set())}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-blue-gray-500 hover:text-blue-gray-700 rounded-lg transition"
                    >
                        <X size={13} />
                        Désélectionner
                    </button>
                    {batchResult && !batchResult.error && (
                        <span className="text-xs text-green-600 font-medium">
                            {batchResult.total_queued} audit{batchResult.total_queued > 1 ? 's' : ''} lancé{batchResult.total_queued > 1 ? 's' : ''}
                            {batchResult.total_duplicate > 0 && `, ${batchResult.total_duplicate} doublon${batchResult.total_duplicate > 1 ? 's' : ''} ignoré${batchResult.total_duplicate > 1 ? 's' : ''}`}
                        </span>
                    )}
                    {batchResult?.error && (
                        <span className="text-xs text-red-600 flex items-center gap-1"><AlertTriangle size={12} />{batchResult.error}</span>
                    )}
                </div>
            )}

            {loading ? (
                <div className="text-center py-16 text-blue-gray-400">Chargement…</div>
            ) : items.length === 0 ? (
                <div className="text-center py-16 text-blue-gray-400">Aucune demande trouvée</div>
            ) : (
                <div className="bg-white/60 backdrop-blur-sm rounded-lg border border-blue-gray-100 shadow-sm overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-blue-gray-50 border-b border-blue-gray-100">
                                <tr>
                                    {/* Colonne checkbox : uniquement sur les listes CSV */}
                                    {!tunnelOnly && (
                                        <th className="px-3 py-3 w-8">
                                            <button
                                                type="button"
                                                title={items.every(i => selectedTokens.has(i.token)) ? 'Tout désélectionner' : 'Tout sélectionner'}
                                                onClick={() => toggleSelectAll(items)}
                                                className="text-blue-gray-400 hover:text-accent transition"
                                            >
                                                {items.length > 0 && items.every(i => selectedTokens.has(i.token))
                                                    ? <CheckSquare size={15} />
                                                    : <Square size={15} />}
                                            </button>
                                        </th>
                                    )}
                                    <Th colKey="url">Site</Th>
                                    <Th colKey="contact">Contact</Th>
                                    <Th colKey="secteur_activite" className="max-w-[140px]">Secteur</Th>
                                    <Th colKey="score_global">Score global</Th>
                                    <Th colKey="cost_usd">Coût IA</Th>
                                    <Th colKey="engagement">Engagement</Th>
                                    <Th colKey="top_tab_clicks">Onglet + vu</Th>
                                    <Th colKey="lead_status">Lead</Th>
                                    <Th colKey="created_at">Date</Th>
                                    <th className="px-4 py-3 text-[10px] font-heading font-semibold text-blue-gray-500 uppercase tracking-wider w-20 text-center">Résultat</th>
                                    <th className="px-4 py-3 text-[10px] font-heading font-semibold text-blue-gray-500 uppercase tracking-wider w-16 text-center">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-blue-gray-50">
                                {items.map(item => {
                                    const st = STATUS_MAP[item.status] || STATUS_MAP.pending;
                                    const isSelected = !tunnelOnly && selectedTokens.has(item.token);
                                    const perfScore = Number(item.pagespeed_average ?? 0);
                                    const hasPerformanceError = item.status === 'done' && perfScore === 0;
                                    const sectorText = String(item.secteur_activite || '').toLowerCase();
                                    const hasIaSectorFallback = sectorText.includes("analysé à partir de votre page d'accueil".toLowerCase())
                                        || sectorText.includes("analyse a partir de votre page d'accueil");
                                    return (
                                        <tr
                                            key={item.id}
                                            className={`hover:bg-accent/[0.03] transition cursor-pointer ${isSelected ? 'bg-accent/[0.05]' : ''} ${(hasPerformanceError || hasIaSectorFallback) ? 'bg-red-50/70' : ''}`}
                                            onClick={() => onSelectProspect(item)}
                                            title={hasPerformanceError
                                                ? 'Performance à 0 détectée (erreur probable)'
                                                : hasIaSectorFallback
                                                    ? "Analyse IA non réalisée (fallback détecté dans le secteur)"
                                                    : undefined}
                                        >
                                            {/* Cellule checkbox : uniquement sur les listes CSV */}
                                            {!tunnelOnly && (
                                                <td className="px-3 py-3">
                                                    <button
                                                        type="button"
                                                        onClick={e => toggleSelect(item.token, e)}
                                                        className={`transition ${isSelected ? 'text-accent' : 'text-blue-gray-300 hover:text-accent'}`}
                                                        title={isSelected ? 'Désélectionner' : 'Sélectionner'}
                                                    >
                                                        {isSelected ? <CheckSquare size={15} /> : <Square size={15} />}
                                                    </button>
                                                </td>
                                            )}
                                            <td className="px-4 py-3">
                                                <div className="font-medium text-blue-gray-800 truncate max-w-[180px] flex items-center gap-1.5">
                                                    {item.url ? (
                                                        <>
                                                            <a
                                                                href={item.url.match(/^https?:\/\//) ? item.url : `https://${item.url}`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                onClick={e => e.stopPropagation()}
                                                                className="truncate hover:text-accent hover:underline"
                                                                title={`Ouvrir ${item.url}`}
                                                            >
                                                                {item.url.replace(/^https?:\/\//, '')}
                                                            </a>
                                                            <a
                                                                href={item.url.match(/^https?:\/\//) ? item.url : `https://${item.url}`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                onClick={e => e.stopPropagation()}
                                                                className="flex-shrink-0 text-blue-gray-400 hover:text-accent"
                                                                title="Ouvrir le site"
                                                            >
                                                                <ExternalLink size={12} />
                                                            </a>
                                                        </>
                                                    ) : (
                                                        <span className="text-blue-gray-400">–</span>
                                                    )}
                                                </div>
                                                <div className="flex items-center gap-1 mt-0.5">
                                                    <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold" style={{ background: st.bg, color: st.text }}>{st.label}</span>
                                                    {hasPerformanceError && (
                                                        <span
                                                            className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-red-100 text-red-700"
                                                            title="Score performance à 0 détecté : erreur probable de mesure"
                                                        >
                                                            Erreur performance
                                                        </span>
                                                    )}
                                                    {hasIaSectorFallback && (
                                                        <span
                                                            className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-red-100 text-red-700"
                                                            title="Secteur basé sur la page d'accueil : l'analyse IA n'a probablement pas été exécutée"
                                                        >
                                                            Erreur IA
                                                        </span>
                                                    )}
                                                    {item.is_duplicate_domain && (
                                                        <span
                                                            className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-100 text-amber-700"
                                                            title={`Doublon détecté (${item.duplicate_domain_count || 2} entrées pour ce domaine)`}
                                                        >
                                                            Doublon
                                                        </span>
                                                    )}
                                                </div>
                                                {item.status === 'processing' && item.audit_progress?.current_label && (
                                                    <div className="mt-1 text-[11px] text-blue-700">
                                                        {item.audit_progress.current_label}
                                                        {typeof item.audit_progress.percent === 'number' ? ` (${item.audit_progress.percent}%)` : ''}
                                                    </div>
                                                )}
                                                {item.status === 'pending' && item.audit_progress?.current_label && (
                                                    <div className="mt-1 text-[11px] text-blue-gray-500">
                                                        {item.audit_progress.current_label}
                                                    </div>
                                                )}
                                                {item.status === 'error' && item.error_message && (
                                                    <div className="mt-1.5 flex items-start gap-1 text-[11px] text-red-600 bg-red-50 border border-red-100 rounded px-2 py-1.5 max-w-[220px]" title={item.error_message}>
                                                        <AlertTriangle size={12} className="flex-shrink-0 mt-0.5" />
                                                        <span className="line-clamp-2 break-words">{item.error_message}</span>
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-4 py-3">
                                                <div className="text-blue-gray-700 font-medium truncate max-w-[160px]">
                                                    {formatProspectDisplayName(item)}
                                                </div>
                                                <div className="text-xs text-blue-gray-400 truncate">{item.email}</div>
                                                {item.phone && <div className="text-xs text-blue-gray-300">{item.phone}</div>}
                                                {item.company && <div className="text-xs text-blue-gray-300 italic">{item.company}</div>}
                                            </td>
                                            <td className="px-4 py-3 max-w-[160px]">
                                                <span
                                                    className={`text-xs line-clamp-2 break-words ${hasIaSectorFallback ? 'text-red-700 font-medium' : 'text-blue-gray-600'}`}
                                                    title={item.secteur_activite || undefined}
                                                >
                                                    {item.secteur_activite?.trim() ? item.secteur_activite : '–'}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3">
                                                <ScorePill
                                                    score={item.score_global}
                                                    showDashWhenZero={item.status !== 'done'}
                                                />
                                            </td>
                                            <td className="px-4 py-3 text-xs text-blue-gray-500">
                                                {item.gemini_usage?.cost_usd != null
                                                    ? `$${Number(item.gemini_usage.cost_usd).toFixed(4)}`
                                                    : '–'}
                                            </td>
                                            <td className="px-4 py-3">
                                                <div className="flex items-center gap-2 text-xs">
                                                    <span className="flex items-center gap-1 text-blue-gray-400" title="Events">
                                                        <MousePointerClick size={11} />
                                                        {item.event_count || 0}
                                                    </span>
                                                    <span className="flex items-center gap-1 text-blue-gray-400" title="Sessions">
                                                        <Eye size={11} />
                                                        {item.session_count || 0}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="px-4 py-3">
                                                {item.top_tab ? (
                                                    <span className="text-xs text-blue-gray-700" title={`${item.top_tab_clicks || 0} vue(s)`}>
                                                        {TOP_TAB_LABELS[item.top_tab] || item.top_tab.replace('tab_', '')}
                                                        {item.top_tab_clicks != null && item.top_tab_clicks > 0 && (
                                                            <span className="ml-1 text-blue-gray-400">({item.top_tab_clicks})</span>
                                                        )}
                                                    </span>
                                                ) : (
                                                    <span className="text-xs text-blue-gray-300">–</span>
                                                )}
                                            </td>
                                            <td className="px-4 py-3">
                                                <LeadStatusBadge status={item.lead_status || 'new'} onClick={e => e.stopPropagation()} />
                                            </td>
                                            <td className="px-4 py-3 text-xs text-blue-gray-500 whitespace-nowrap">
                                                {item.created_at ? new Date(item.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '–'}
                                            </td>
                                            <td className="px-4 py-3 text-center">
                                                <a
                                                    href={`/audit/${item.token}${STAFF_AUDIT_QUERY}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    onClick={async (e) => {
                                                        e.stopPropagation();
                                                        if ((item.lead_status || 'new') === 'new' && onListRefresh) {
                                                            try {
                                                                await axiosService.patch(`${API_BASE}/api/public/admin/prospects/${item.token}/lead`, { lead_status: 'in_progress' });
                                                                onListRefresh();
                                                            } catch (_) {}
                                                        }
                                                    }}
                                                    className="inline-flex items-center gap-1 text-xs font-medium text-accent hover:underline"
                                                    title="Ouvrir le résultat d'audit"
                                                >
                                                    <ExternalLink size={14} />
                                                    Lien
                                                </a>
                                            </td>
                                            <td className="px-4 py-3 text-center">
                                                <button
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setProspectToDelete(item);
                                                    }}
                                                    className="inline-flex items-center justify-center p-1.5 rounded-lg text-blue-gray-400 hover:text-red-500 hover:bg-red-50 transition"
                                                    title="Supprimer le lead"
                                                >
                                                    <Trash2 size={16} />
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    {total > PAGE_SIZE && (
                        <div className="flex items-center justify-between px-4 py-3 border-t border-blue-gray-100">
                            <button
                                onClick={() => setPage(p => Math.max(1, p - 1))}
                                disabled={page === 1}
                                className="px-3 py-1 text-sm border border-blue-gray-200 rounded-lg text-blue-gray-500 disabled:opacity-40 hover:bg-blue-gray-50"
                            >Précédent</button>
                            <span className="text-xs text-blue-gray-400">Page {page} / {Math.max(1, Math.ceil(total / PAGE_SIZE))}</span>
                            <button
                                onClick={() => setPage(p => p + 1)}
                                disabled={page * PAGE_SIZE >= total}
                                className="px-3 py-1 text-sm border border-blue-gray-200 rounded-lg text-blue-gray-500 disabled:opacity-40 hover:bg-blue-gray-50"
                            >Suivant</button>
                        </div>
                    )}
                </div>
            )}

            <Modal
                open={!!prospectToDelete}
                onClose={() => !deleteLoading && setProspectToDelete(null)}
                title="Supprimer ce lead ?"
                size="sm"
            >
                {prospectToDelete && (
                    <>
                        <p className="text-sm text-blue-gray-600 mb-4">
                            Vous êtes sur le point de supprimer définitivement le lead pour{' '}
                            <span className="font-medium text-blue-gray-800">
                                {prospectToDelete.url?.replace(/^https?:\/\//, '') || prospectToDelete.email}
                            </span>
                            . Cette action est irréversible.
                        </p>
                        <div className="flex justify-end gap-2">
                            <Button
                                type="button"
                                variant="secondary"
                                onClick={() => setProspectToDelete(null)}
                                disabled={deleteLoading}
                            >
                                Annuler
                            </Button>
                            <Button
                                type="button"
                                variant="primary"
                                onClick={async () => {
                                    setDeleteLoading(true);
                                    try {
                                        await axiosService.delete(`${API_BASE}/api/public/admin/prospects/${prospectToDelete.token}`);
                                        if (selectedToken === prospectToDelete.token) onSelectProspect(null);
                                        setProspectToDelete(null);
                                        fetchProspects();
                                    } catch (err) {
                                        console.error(err);
                                        alert(err.response?.data?.detail || 'Erreur lors de la suppression');
                                    } finally {
                                        setDeleteLoading(false);
                                    }
                                }}
                                disabled={deleteLoading}
                            >
                                {deleteLoading ? 'Suppression…' : 'Supprimer'}
                            </Button>
                        </div>
                    </>
                )}
            </Modal>

            <Modal
                open={exportModalOpen}
                onClose={() => !exportLoading && setExportModalOpen(false)}
                title="Exporter le CSV"
                size="md"
            >
                <div className="space-y-4">
                    <p className="text-sm text-blue-gray-600">
                        Les colonnes importees d'origine seront toujours conservees. Choisissez les colonnes d'audit a ajouter.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-80 overflow-auto pr-1">
                        {EXPORT_EXTRA_COLUMNS.map(col => {
                            const checked = selectedExportExtras.has(col.key);
                            return (
                                <label
                                    key={col.key}
                                    className={`group flex items-center gap-2.5 rounded-lg border px-3 py-2 text-sm cursor-pointer transition ${checked
                                        ? 'border-accent/50 bg-accent/5 text-blue-gray-800'
                                        : 'border-blue-gray-200 bg-white text-blue-gray-700 hover:border-blue-gray-300 hover:bg-blue-gray-50/60'
                                        }`}
                                >
                                    <input
                                        type="checkbox"
                                        checked={checked}
                                        className="sr-only peer"
                                        onChange={(e) => {
                                            const isChecked = e.target.checked;
                                            setSelectedExportExtras(prev => {
                                                const next = new Set(prev);
                                                if (isChecked) next.add(col.key);
                                                else next.delete(col.key);
                                                return next;
                                            });
                                        }}
                                    />
                                    <span
                                        className={`h-4 w-4 rounded border flex items-center justify-center transition ${checked
                                            ? 'bg-accent border-accent text-white'
                                            : 'bg-white border-blue-gray-300 text-transparent'
                                            } peer-focus:ring-2 peer-focus:ring-accent/30`}
                                        aria-hidden="true"
                                    >
                                        <Check size={11} strokeWidth={3} />
                                    </span>
                                    <span className="leading-tight">{col.label}</span>
                                </label>
                            );
                        })}
                    </div>
                    <div className="flex items-center justify-between pt-1">
                        <button
                            type="button"
                            className="text-xs font-medium text-blue-gray-500 hover:text-blue-gray-700 hover:underline"
                            onClick={() => setSelectedExportExtras(new Set(EXPORT_EXTRA_COLUMNS.map(c => c.key)))}
                        >
                            Tout sélectionner
                        </button>
                        <button
                            type="button"
                            className="text-xs font-medium text-blue-gray-500 hover:text-blue-gray-700 hover:underline"
                            onClick={() => setSelectedExportExtras(new Set())}
                        >
                            Tout désélectionner
                        </button>
                    </div>
                    <div className="flex justify-end gap-2 pt-1">
                        <Button
                            type="button"
                            variant="secondary"
                            onClick={() => setExportModalOpen(false)}
                            disabled={exportLoading}
                        >
                            Annuler
                        </Button>
                        <Button
                            type="button"
                            variant="primary"
                            onClick={async () => {
                                await handleExportCsv(Array.from(selectedExportExtras));
                                setExportModalOpen(false);
                            }}
                            disabled={exportLoading}
                        >
                            {exportLoading ? 'Export en cours...' : 'Exporter'}
                        </Button>
                    </div>
                </div>
            </Modal>
        </div>
    );
}

// ── Onglet Comportement ──

const LABEL_NAMES = {
    tab_synthese: 'Synthèse', tab_seo: 'SEO',
    tab_performance: 'Performance', tab_errors: 'Erreurs',
    tab_technique: 'Technique', tab_marche: 'Marché',
    tab_ia: 'Analyse IA',
    cta_full_audit: 'CTA Audit complet', cta_appointment: 'CTA RDV', cta_sticky_bar: 'CTA barre sticky',
    cta_blurred_synthese: 'CTA zone floutée (synthèse)', cta_blurred_marche: 'CTA zone floutée (marché)',
    cta_blurred_seo_headings: 'CTA zone floutée (SEO headings)', cta_blurred_performance: 'CTA zone floutée (perf)',
    cta_blurred_technique: 'CTA zone floutée (technique)', cta_blurred_ia: 'CTA zone floutée (IA)',
    conversion_modal_cta: 'CTA modal conversion', marche_blocked_audit_complet: 'CTA audit complet (marché bloqué)',
    page_view: 'Vue de page', result_page_view: 'Vue résultat', form_submit: 'Soumission formulaire',
    landing_page_view: 'Vue landing', landing_step_2_view: 'Landing étape 2 vue', landing_step_1_url: 'Landing URL soumise',
    landing_form_contact_submit: 'Formulaire contact soumis', landing_form_contact_success: 'Formulaire contact OK',
    landing_form_contact_error: 'Formulaire contact erreur', landing_step_2_back: 'Landing retour étape 1',
    landing_modal_privacy_open: 'Ouverture politique confidentialité',
    landing_exit: 'Sortie landing',
    session_end: 'Fin de session', return_visit: 'Visite retour',
};

const TAB_SHORT_LABELS = {
    tab_seo: 'SEO', tab_performance: 'Performance', tab_technique: 'Technique',
    tab_marche: 'Marché', tab_ia: 'Analyse IA', tab_synthese: 'Synthèse', tab_errors: 'Erreurs',
};

function EngagedProspectCard({ prospect, metric, metricLabel, metricIcon: MetricIcon, metricColor, onViewProspect }) {
    const displayName = prospect.name || prospect.email?.split('@')[0] || '—';
    const domain = prospect.url?.replace(/^https?:\/\//, '').replace(/\/$/, '') || '—';
    const scoreColor = (prospect.score_global ?? -1) >= 90 ? '#3bb77e' : (prospect.score_global ?? -1) >= 60 ? '#d97706' : '#ef4444';
    const topTabLabel = prospect.top_tab ? (TAB_SHORT_LABELS[prospect.top_tab] || prospect.top_tab) : null;
    return (
        <div className="bg-white rounded-xl border border-blue-gray-100 shadow-sm p-3.5 flex flex-col gap-2.5 hover:shadow-md transition-shadow">
            {/* Nom + domaine */}
            <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                    <div className="text-xs font-semibold text-blue-gray-800 truncate">{displayName}</div>
                    <div className="text-[11px] text-blue-gray-400 truncate mt-0.5">{domain}</div>
                </div>
                {prospect.score_global != null && (
                    <span className="shrink-0 text-[10px] font-bold px-1.5 py-0.5 rounded-md" style={{ color: scoreColor, background: `${scoreColor}18` }}>
                        {Math.round(prospect.score_global)}/100
                    </span>
                )}
            </div>

            {/* Tags */}
            <div className="flex items-center gap-1.5 flex-wrap">
                <div className="inline-flex items-center gap-1 px-2 py-1 rounded-lg" style={{ background: `${metricColor}12` }}>
                    <MetricIcon size={11} style={{ color: metricColor }} />
                    <span className="text-[11px] font-bold" style={{ color: metricColor }}>{metric}</span>
                    {metricLabel && <span className="text-[11px] text-blue-gray-400">{metricLabel}</span>}
                </div>
                {topTabLabel && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-1 rounded-lg bg-blue-gray-50 text-blue-gray-500 border border-blue-gray-100">
                        <Layers size={9} />
                        {topTabLabel}
                    </span>
                )}
            </div>

            {/* Action */}
            <button
                type="button"
                onClick={() => onViewProspect?.(prospect)}
                className="w-full text-[11px] font-semibold text-accent bg-accent/8 hover:bg-accent/15 border border-accent/20 py-1.5 rounded-lg transition"
            >
                Voir la fiche →
            </button>
        </div>
    );
}

function ComportementTab({ prospectListId = null, tunnelOnly = false, onViewProspect }) {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [engaged, setEngaged] = useState(null);
    const [periodDays, setPeriodDays] = useState(7);
    const periodOptions = [7, 30, 90];

    useEffect(() => {
        setLoading(true);
        const params = {};
        if (prospectListId != null) params.prospect_list_id = prospectListId;
        else if (tunnelOnly) params.tunnel_only = true;
        params.period_days = periodDays;
        axiosService.get(`${API_BASE}/api/public/admin/prospects/stats`, { params })
            .then(res => setStats(res.data))
            .catch(console.error)
            .finally(() => setLoading(false));
    }, [prospectListId, tunnelOnly, periodDays]);

    useEffect(() => {
        const params = { limit: 8 };
        if (prospectListId != null) params.prospect_list_id = prospectListId;
        else if (tunnelOnly) params.tunnel_only = true;
        axiosService.get(`${API_BASE}/api/public/admin/prospects/top-engaged`, { params })
            .then(res => setEngaged(res.data))
            .catch(() => setEngaged(null));
    }, [prospectListId, tunnelOnly]);

    if (loading) return <div className="text-center py-16 text-blue-gray-400">Chargement…</div>;
    if (!stats) return <div className="text-center py-16 text-blue-gray-400">Données indisponibles</div>;

    const tabClicks = (stats.events_by_label || []).filter(e => e.label?.startsWith('tab_'));
    const ctaClicks = (stats.events_by_label || []).filter(e => e.label?.startsWith('cta_') || e.label === 'conversion_modal_cta');
    const maxTabCount = Math.max(...tabClicks.map(e => e.count), 1);
    const maxCtaCount = Math.max(...ctaClicks.map(e => e.count), 1);

    const totalProspects = stats.total_audits || 0;
    const totalCompleted = stats.total_done || 0;
    const auditsViewed = stats.audits_viewed_done ?? 0;
    const viewRateVsDone = stats.view_rate_vs_done ?? 0;
    const totalContacts = ctaClicks.reduce((sum, e) => sum + (e.count || 0), 0);
    const completionRate = totalProspects > 0 ? Math.round((totalCompleted / totalProspects) * 100) : 0;
    const contactRate = totalCompleted > 0 ? Math.round((totalContacts / totalCompleted) * 100) : 0;
    const contactRateVsViewed = auditsViewed > 0 ? Math.round((totalContacts / auditsViewed) * 100) : 0;

    return (
        <div className="flex flex-col gap-6">

            {/* Entonnoir compact */}
            <div className="bg-white/60 backdrop-blur-sm rounded-lg border border-blue-gray-100 shadow-sm p-4">
                <div className="flex items-center gap-2 mb-4">
                    <TrendingUp size={13} className="text-accent" />
                    <span className="text-xs font-semibold text-blue-gray-600">Entonnoir de conversion</span>
                    {stats.avg_score_global != null && (
                        <span className="ml-auto text-[11px] text-blue-gray-400">
                            Note SEO moy. <span className="font-semibold text-blue-gray-700">{stats.avg_score_global}/100</span>
                        </span>
                    )}
                </div>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-blue-gray-100 rounded-lg overflow-hidden">
                    {[
                        {
                            icon: Users, label: 'Demandes', value: totalProspects,
                            rate: null, rateLabel: 'Prospects / lancements',
                            valueColor: 'text-blue-gray-900', iconColor: 'text-blue-gray-400',
                        },
                        {
                            icon: CheckCircle, label: 'Audits terminés', value: totalCompleted,
                            rate: completionRate, rateLabel: '% des demandes',
                            valueColor: 'text-blue-gray-900', iconColor: 'text-green-500',
                        },
                        {
                            icon: Eye, label: 'Rapports consultés', value: auditsViewed,
                            rate: viewRateVsDone, rateLabel: '% des terminés',
                            valueColor: 'text-blue-gray-900', iconColor: 'text-violet-500',
                        },
                        {
                            icon: Phone, label: 'Contacts / RDV', value: totalContacts,
                            rate: auditsViewed > 0 ? contactRateVsViewed : null,
                            rateLabel: '% de ceux qui ont vu',
                            valueColor: 'text-emerald-700', iconColor: 'text-emerald-600',
                        },
                    ].map(({ icon: Icon, label, value, rate, rateLabel, valueColor, iconColor }, i) => (
                        <div key={i} className="bg-white px-4 py-3 flex flex-col gap-1">
                            <div className={`flex items-center gap-1.5 ${iconColor}`}>
                                <Icon size={12} />
                                <span className="text-[11px] text-blue-gray-500 font-medium">{label}</span>
                            </div>
                            <div className={`text-2xl font-extrabold leading-none mt-0.5 ${valueColor}`}>{value}</div>
                            <div className="text-[10px] text-blue-gray-400 mt-0.5">
                                {rate != null
                                    ? <><span className="font-semibold text-blue-gray-600">{rate}%</span> {rateLabel}</>
                                    : rateLabel
                                }
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Prospects les plus engagés */}
            {engaged && (
                <div className="bg-white/60 backdrop-blur-sm rounded-lg border border-blue-gray-100 shadow-sm p-5">
                    <h3 className="font-heading font-bold text-blue-gray-800 text-sm mb-1 flex items-center gap-2">
                        <TrendingUp size={16} className="text-accent" />
                        Prospects les plus engagés
                    </h3>
                    <p className="text-xs text-blue-gray-400 mb-5">Les prospects qui ont montré le plus d'intérêt — à contacter en priorité</p>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {/* Retours sur l'audit */}
                        <div>
                            <div className="flex items-center gap-2 mb-3">
                                <div className="w-6 h-6 rounded-md flex items-center justify-center bg-amber-50">
                                    <RotateCcw size={12} className="text-amber-600" />
                                </div>
                                <span className="text-xs font-semibold text-blue-gray-700">Revenus sur leur audit</span>
                            </div>
                            {engaged.return_visitors.length === 0 ? (
                                <p className="text-xs text-blue-gray-300 py-3">Aucun retour enregistré</p>
                            ) : (
                                <div className="flex flex-col gap-2">
                                    {engaged.return_visitors.map(p => (
                                        <EngagedProspectCard key={p.token} prospect={p}
                                            metric={`×${p.metric}`} metricLabel="retour(s)"
                                            metricIcon={RotateCcw} metricColor="#b45309"
                                            onViewProspect={onViewProspect} />
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Clics CTA */}
                        <div>
                            <div className="flex items-center gap-2 mb-3">
                                <div className="w-6 h-6 rounded-md flex items-center justify-center bg-green-50">
                                    <Phone size={12} className="text-green-600" />
                                </div>
                                <span className="text-xs font-semibold text-blue-gray-700">Ont demandé à être contactés</span>
                            </div>
                            {engaged.cta_clickers.length === 0 ? (
                                <p className="text-xs text-blue-gray-300 py-3">Aucun clic CTA enregistré</p>
                            ) : (
                                <div className="flex flex-col gap-2">
                                    {engaged.cta_clickers.map(p => (
                                        <EngagedProspectCard key={p.token} prospect={p}
                                            metric={`×${p.metric}`} metricLabel="clic(s)"
                                            metricIcon={Phone} metricColor="#16a34a"
                                            onViewProspect={onViewProspect} />
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Temps passé */}
                        <div>
                            <div className="flex items-center gap-2 mb-3">
                                <div className="w-6 h-6 rounded-md flex items-center justify-center bg-blue-50">
                                    <Timer size={12} className="text-blue-600" />
                                </div>
                                <span className="text-xs font-semibold text-blue-gray-700">Temps passé le plus long</span>
                            </div>
                            {engaged.time_spent.length === 0 ? (
                                <p className="text-xs text-blue-gray-300 py-3">Aucune donnée de durée</p>
                            ) : (
                                <div className="flex flex-col gap-2">
                                    {engaged.time_spent.map(p => (
                                        <EngagedProspectCard key={p.token} prospect={p}
                                            metric={formatDuration(p.metric)} metricLabel=""
                                            metricIcon={Timer} metricColor="#2563eb"
                                            onViewProspect={onViewProspect} />
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Ce qui intéresse + Actions commerciales */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white/60 backdrop-blur-sm rounded-lg border border-blue-gray-100 shadow-sm p-5">
                    <h3 className="font-heading font-bold text-blue-gray-800 text-sm mb-1 flex items-center gap-2">
                        <Layers size={16} className="text-accent" />
                        Ce qui intéresse vos prospects
                    </h3>
                    <p className="text-xs text-blue-gray-400 mb-4">Sections les plus consultées dans les rapports d'audit</p>
                    {tabClicks.length === 0 ? (
                        <div className="text-sm text-blue-gray-300 py-4 text-center">Aucune donnée</div>
                    ) : (
                        <div className="space-y-3">
                            {tabClicks.map(e => {
                                const pct = Math.round(e.count / maxTabCount * 100);
                                return (
                                    <div key={e.label} className="flex items-center gap-3">
                                        <div className="flex-1 min-w-0">
                                            <div className="flex justify-between items-center mb-1">
                                                <span className="text-xs text-blue-gray-600 font-medium">{getEventLabelDisplay(e.label)}</span>
                                                <span className="text-xs font-bold text-blue-gray-800">{e.count}</span>
                                            </div>
                                            <div className="h-1.5 bg-blue-gray-100 rounded-full overflow-hidden">
                                                <div className="h-full rounded-full bg-accent/60 transition-all" style={{ width: `${pct}%` }} />
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                <div className="bg-white/60 backdrop-blur-sm rounded-lg border border-blue-gray-100 shadow-sm p-5">
                    <h3 className="font-heading font-bold text-blue-gray-800 text-sm mb-1 flex items-center gap-2">
                        <ArrowUpRight size={16} className="text-green-600" />
                        Actions commerciales
                    </h3>
                    <p className="text-xs text-blue-gray-400 mb-4">Clics sur les boutons de contact et de prise de RDV</p>
                    {ctaClicks.length === 0 ? (
                        <div className="text-sm text-blue-gray-300 py-4 text-center">Aucun clic enregistré</div>
                    ) : (
                        <div className="space-y-3">
                            {ctaClicks.map(e => {
                                const pct = Math.round(e.count / maxCtaCount * 100);
                                return (
                                    <div key={e.label} className="flex items-center gap-3">
                                        <div className="flex-1">
                                            <div className="flex justify-between items-center mb-1">
                                                <span className="text-xs text-blue-gray-600 font-medium">{getEventLabelDisplay(e.label)}</span>
                                                <span className="text-xs font-bold text-blue-gray-800">{e.count}</span>
                                            </div>
                                            <div className="h-1.5 bg-blue-gray-100 rounded-full overflow-hidden">
                                                <div className="h-full rounded-full bg-green-400 transition-all" style={{ width: `${pct}%` }} />
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>

            <div className="flex justify-end">
                <a
                    href="#demandes-period-table"
                    className="inline-flex items-center gap-2 text-xs font-semibold text-accent bg-accent/10 hover:bg-accent/15 transition px-3 py-1.5 rounded-lg"
                >
                    <CalendarDays size={14} />
                    Voir le tableau des demandes
                </a>
            </div>

            {/* Demandes sur période sélectionnée (placé en bas de page) */}
            {stats.audits_per_day && stats.audits_per_day.length > 0 && (() => {
                const totalPeriod = stats.audits_per_day.reduce((acc, d) => acc + (d.count || 0), 0);
                const maxPeriodCount = Math.max(...stats.audits_per_day.map(x => x.count || 0), 1);
                const dayNames = ['Dim.', 'Lun.', 'Mar.', 'Mer.', 'Jeu.', 'Ven.', 'Sam.'];
                return (
                    <div id="demandes-period-table" className="bg-white/60 backdrop-blur-sm rounded-lg border border-blue-gray-100 shadow-sm p-5">
                        <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
                            <h3 className="font-heading font-bold text-blue-gray-800 text-sm flex items-center gap-2">
                                <CalendarDays size={16} className="text-accent" />
                                Demandes ({stats.period_days || periodDays} derniers jours)
                            </h3>
                            <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-xs font-semibold text-accent bg-accent/10 px-2.5 py-1 rounded-lg">
                                    {totalPeriod} demande{totalPeriod !== 1 ? 's' : ''} sur la période
                                </span>
                                <div className="inline-flex rounded-lg border border-blue-gray-200 bg-white p-0.5">
                                    {periodOptions.map(option => (
                                        <button
                                            key={option}
                                            type="button"
                                            onClick={() => setPeriodDays(option)}
                                            className={`px-2.5 py-1 text-xs rounded-md transition ${periodDays === option
                                                ? 'bg-accent text-white'
                                                : 'text-blue-gray-600 hover:bg-blue-gray-50'
                                                }`}
                                        >
                                            {option}j
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full min-w-[540px] text-xs">
                                <thead>
                                    <tr className="text-blue-gray-400">
                                        <th className="text-left font-semibold py-2 pr-3">Jour</th>
                                        <th className="text-left font-semibold py-2 pr-3">Date</th>
                                        <th className="text-right font-semibold py-2 pr-3">Demandes</th>
                                        <th className="text-right font-semibold py-2">Part</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-blue-gray-100/70">
                                    {stats.audits_per_day.map(d => {
                                        const date = new Date(d.date);
                                        const dayLabel = dayNames[date.getDay()];
                                        const dateStr = date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
                                        const count = d.count || 0;
                                        const ratio = maxPeriodCount > 0 ? Math.round((count / maxPeriodCount) * 100) : 0;
                                        return (
                                            <tr key={d.date}>
                                                <td className="py-2 pr-3 text-blue-gray-600 font-medium">{dayLabel}</td>
                                                <td className="py-2 pr-3 text-blue-gray-500">{dateStr}</td>
                                                <td className="py-2 pr-3 text-right text-blue-gray-800 font-semibold">{count}</td>
                                                <td className="py-2">
                                                    <div className="flex items-center justify-end gap-2">
                                                        <div className="w-24 h-2 rounded-full bg-blue-gray-100 overflow-hidden">
                                                            <div className="h-full bg-accent/80 rounded-full" style={{ width: `${ratio}%` }} />
                                                        </div>
                                                        <span className="text-blue-gray-500 w-9 text-right">{ratio}%</span>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                );
            })()}
        </div>
    );
}

// ── Diagnostic disponibilité modèles Gemini ──

const MODEL_STATUS_CONFIG = {
    ok:          { label: 'Disponible',    bg: 'bg-green-50',      border: 'border-green-200',     text: 'text-green-700',      dot: 'bg-green-500'   },
    quota:       { label: 'Quota épuisé', bg: 'bg-red-50',        border: 'border-red-200',       text: 'text-red-700',        dot: 'bg-red-500'     },
    unavailable: { label: 'Indisponible', bg: 'bg-blue-gray-50',  border: 'border-blue-gray-100', text: 'text-blue-gray-400',  dot: 'bg-blue-gray-300' },
    error:       { label: 'Erreur',       bg: 'bg-amber-50',      border: 'border-amber-200',     text: 'text-amber-700',      dot: 'bg-amber-400'   },
};

function GeminiModelProbe() {
    const [probeData, setProbeData] = useState(null);
    const [probeLoading, setProbeLoading] = useState(false);
    const [probeError, setProbeError] = useState(null);

    const runProbe = async () => {
        setProbeLoading(true);
        setProbeError(null);
        setProbeData(null);
        try {
            const res = await axiosService.get(`${API_BASE}/api/public/admin/gemini/test-models`);
            setProbeData(res.data);
        } catch (e) {
            setProbeError(e.response?.data?.detail || e.message || 'Erreur lors du diagnostic');
        } finally {
            setProbeLoading(false);
        }
    };

    const results = probeData?.results || [];
    const hasQuota = results.some(r => r.status === 'quota');
    const okCount = results.filter(r => r.status === 'ok').length;
    const defaultModel = probeData?.default_model;

    // Masquer les lignes « indisponible » sans infos RPM/RPD (bruit)
    const visibleResults = results.filter(r => r.status !== 'unavailable' || r.rpm != null || r.rpd != null);

    return (
        <div className="border-t border-blue-gray-100 pt-5 mt-5">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <div>
                    <h3 className="text-sm font-semibold text-blue-gray-800 flex items-center gap-2">
                        <BrainCircuit size={15} className="text-violet-500" />
                        Disponibilité des modèles Gemini
                    </h3>
                    <p className="text-xs text-blue-gray-500 mt-0.5 max-w-2xl">
                        {probeData?.probe_scope || 'Uniquement les modèles utilisés par l’app (même chaîne que les audits IA / JSON). Hors embeddings, Imagen, TTS. RPM/RPD : quotas free tier ; RPD reset à minuit (heure Pacifique).'}
                    </p>
                </div>
                <button
                    type="button"
                    onClick={runProbe}
                    disabled={probeLoading}
                    className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium bg-violet-600 hover:bg-violet-700 text-white rounded-lg transition disabled:opacity-60"
                >
                    {probeLoading ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
                    {probeLoading ? 'Test en cours…' : 'Tester les modèles'}
                </button>
            </div>

            {probeError && (
                <div className="flex items-center gap-2 text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2 mb-3">
                    <AlertTriangle size={13} className="flex-shrink-0" />
                    {probeError}
                </div>
            )}

            {probeData && (
                <>
                    {/* Résumé + lien */}
                    <div className={`flex items-start gap-2 text-xs rounded-lg px-3 py-2 mb-3 border ${hasQuota ? 'text-amber-700 bg-amber-50 border-amber-200' : 'text-green-700 bg-green-50 border-green-200'}`}>
                        <AlertTriangle size={13} className="flex-shrink-0 mt-0.5" />
                        <span>
                            {hasQuota
                                ? <>Quota RPM/RPD épuisé sur certains modèles. L'app bascule automatiquement sur un modèle disponible. {okCount === 0 && <strong>Aucun modèle disponible en ce moment — attends le reset à minuit (heure Pacifique).</strong>}</>
                                : <>Tous les modèles testés répondent normalement ({okCount} disponible{okCount > 1 ? 's' : ''}).</>
                            }
                            {' '}<a href="https://aistudio.google.com/rate-limit" target="_blank" rel="noopener noreferrer" className="underline font-medium">Voir tes quotas réels sur AI Studio</a>
                        </span>
                    </div>

                    {/* Modèle actif par défaut */}
                    {defaultModel && (
                        <div className="text-xs text-blue-gray-500 mb-3 flex items-center gap-1.5">
                            <span className="font-medium text-blue-gray-700">Modèle actif :</span>
                            <code className="bg-violet-50 text-violet-700 px-1.5 py-0.5 rounded font-mono">{defaultModel}</code>
                        </div>
                    )}

                    {/* Tableau modèles */}
                    <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                            <thead>
                                <tr className="text-blue-gray-400 border-b border-blue-gray-100">
                                    <th className="text-left font-semibold py-1.5 pr-3">Modèle</th>
                                    <th className="text-center font-semibold py-1.5 px-2">Statut</th>
                                    <th className="text-center font-semibold py-1.5 px-2">RPM free</th>
                                    <th className="text-center font-semibold py-1.5 px-2">RPD free</th>
                                    <th className="text-left font-semibold py-1.5 pl-2">Info</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-blue-gray-50">
                                {visibleResults.map(r => {
                                    const cfg = MODEL_STATUS_CONFIG[r.status] || MODEL_STATUS_CONFIG.error;
                                    const isDefault = r.model === defaultModel;
                                    return (
                                        <tr key={r.model} className={isDefault ? 'bg-violet-50/60' : ''}>
                                            <td className="py-2 pr-3">
                                                <span className="font-mono text-blue-gray-700">{r.model}</span>
                                                {isDefault && <span className="ml-1.5 text-[10px] font-semibold text-violet-600 bg-violet-100 px-1.5 py-0.5 rounded">actif</span>}
                                            </td>
                                            <td className="py-2 px-2 text-center">
                                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${cfg.bg} ${cfg.border} ${cfg.text}`}>
                                                    <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
                                                    {cfg.label}
                                                </span>
                                            </td>
                                            <td className="py-2 px-2 text-center text-blue-gray-600 font-medium">
                                                {r.rpm != null ? r.rpm : <span className="text-blue-gray-300">–</span>}
                                            </td>
                                            <td className={`py-2 px-2 text-center font-bold ${r.rpd >= 1000 ? 'text-green-600' : r.rpd != null ? 'text-blue-gray-700' : ''}`}>
                                                {r.rpd != null ? r.rpd.toLocaleString('fr-FR') : <span className="text-blue-gray-300 font-normal">–</span>}
                                            </td>
                                            <td className="py-2 pl-2 text-blue-gray-400 max-w-[160px] truncate">
                                                {r.status === 'quota' && r.retry_after
                                                    ? <span className="text-red-600">Réessayer dans {r.retry_after}s</span>
                                                    : r.status === 'quota'
                                                        ? <span className="text-red-600">Reset à minuit (PT)</span>
                                                        : r.error
                                                            ? <span title={r.error} className="truncate">{r.error}</span>
                                                            : null}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </>
            )}
        </div>
    );
}


// ── Onglet Configuration ──

function ConfigurationTab({ section: configSection = 'keys' }) {
    const [config, setConfig] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [testLoading, setTestLoading] = useState(false);
    const [testMessage, setTestMessage] = useState(null);
    const [message, setMessage] = useState(null);
    const [leadEmailsText, setLeadEmailsText] = useState('');
    const [access, setAccess] = useState('all');
    const [previewFirstName, setPreviewFirstName] = useState('Elisa');
    const [previewUrl, setPreviewUrl] = useState('https://www.example.com');
    const [previewScore, setPreviewScore] = useState(64);
    const [previewEmailHtml, setPreviewEmailHtml] = useState('');
    const [previewLoading, setPreviewLoading] = useState(false);
    const [sendTestAuditLoading, setSendTestAuditLoading] = useState(false);
    const [previewTestMessage, setPreviewTestMessage] = useState(null);
    const [smtpForm, setSmtpForm] = useState({
        smtp_host: '',
        smtp_port: 587,
        smtp_user: '',
        smtp_password: '',
        smtp_from: '',
        smtp_from_name: 'Ascanio',
        public_base_url: '',
        audit_cta_url: '',
    });
    const [pagespeedKey, setPagespeedKey] = useState('');
    const [pagespeedQuotaLimit, setPagespeedQuotaLimit] = useState(25000);
    const [geminiKey, setGeminiKey] = useState('');
    const [geminiQuotaLimit, setGeminiQuotaLimit] = useState(1500);
    const [configKeyModal, setConfigKeyModal] = useState(null); // 'pagespeed' | 'gemini' | null

    const fetchConfig = useCallback(async () => {
        setLoading(true);
        setMessage(null);
        try {
            const res = await axiosService.get(`${API_BASE}/api/public/admin/prospects/config`);
            const data = res.data;
            setLeadEmailsText((data.lead_notification_emails || []).join('\n'));
            setAccess(data.prospect_list_access || 'all');
            setSmtpForm({
                smtp_host: data.smtp_host || '',
                smtp_port: data.smtp_port ?? 587,
                smtp_user: data.smtp_user || '',
                smtp_password: '',
                smtp_from: data.smtp_from || '',
                smtp_from_name: data.smtp_from_name || 'Metrics',
                public_base_url: data.public_base_url || '',
                audit_cta_url: data.audit_cta_url || '',
            });
            setPagespeedQuotaLimit(data.pagespeed_quota_limit ?? 25000);
            setGeminiQuotaLimit(data.gemini_quota_limit ?? 1500);
            if (!data.pagespeed_configured) setPagespeedKey('');
            if (!data.gemini_configured) setGeminiKey('');
            setConfig(data);
        } catch (e) {
            console.error(e);
            setMessage({ type: 'error', text: 'Impossible de charger la configuration' });
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchConfig(); }, [fetchConfig]);

    const handleSave = async () => {
        setSaving(true);
        setMessage(null);
        try {
            const leadEmails = leadEmailsText
                .split(/[\n,;]+/)
                .map((e) => e.trim().toLowerCase())
                .filter((e) => e && e.includes('@'));
            const body = {
                lead_notification_emails: leadEmails,
                prospect_list_access: access,
                smtp_host: smtpForm.smtp_host || null,
                smtp_port: smtpForm.smtp_port || 587,
                smtp_user: smtpForm.smtp_user || null,
                smtp_from: smtpForm.smtp_from || null,
                smtp_from_name: smtpForm.smtp_from_name || null,
                public_base_url: smtpForm.public_base_url || null,
                audit_cta_url: smtpForm.audit_cta_url || null,
                pagespeed_api_key: pagespeedKey.trim() || null,
                pagespeed_quota_limit: pagespeedQuotaLimit >= 0 ? pagespeedQuotaLimit : null,
                gemini_api_key: geminiKey.trim() || null,
                gemini_quota_limit: geminiQuotaLimit >= 0 ? geminiQuotaLimit : null,
            };
            if (smtpForm.smtp_password && smtpForm.smtp_password.trim()) {
                body.smtp_password = smtpForm.smtp_password;
            }
            const res = await axiosService.put(`${API_BASE}/api/public/admin/prospects/config`, body);
            const count = res.data?.lead_notification_emails_count ?? leadEmails.length;
            await fetchConfig();
            if (count > 0) {
                setMessage({ type: 'success', text: `Configuration enregistrée. ${count} adresse(s) recevront les notifications de nouveaux leads.` });
            } else {
                setMessage({ type: 'success', text: 'Configuration enregistrée. Aucune adresse configurée : vous ne recevrez pas d’email à chaque nouveau lead (configurez « Adresses à notifier » si besoin).' });
            }
        } catch (e) {
            console.error(e);
            setMessage({ type: 'error', text: e.response?.data?.detail || 'Erreur lors de l\'enregistrement' });
        } finally {
            setSaving(false);
        }
    };

    const handleTestSend = async () => {
        setTestMessage(null);
        setTestLoading(true);
        try {
            await axiosService.post(`${API_BASE}/api/public/admin/prospects/config/test`);
            setTestMessage({ type: 'success', text: 'Email de test envoyé à votre adresse. Vérifiez votre boîte de réception.' });
        } catch (e) {
            setTestMessage({ type: 'error', text: e.response?.data?.detail || 'Échec du test d\'envoi.' });
        } finally {
            setTestLoading(false);
        }
    };

    const fetchPreviewEmail = useCallback(async () => {
        setPreviewLoading(true);
        setPreviewTestMessage(null);
        try {
            const res = await axiosService.get(`${API_BASE}/api/public/admin/prospects/preview-audit-email`, {
                params: {
                    first_name: previewFirstName || undefined,
                    url: previewUrl || undefined,
                    score_global: previewScore !== '' ? Number(previewScore) : undefined,
                },
            });
            setPreviewEmailHtml(res.data.html || '');
        } catch (e) {
            setPreviewTestMessage({ type: 'error', text: e.response?.data?.detail || 'Impossible de charger l\'aperçu.' });
        } finally {
            setPreviewLoading(false);
        }
    }, [previewFirstName, previewUrl, previewScore]);

    const sendTestAuditEmail = async () => {
        setSendTestAuditLoading(true);
        setPreviewTestMessage(null);
        try {
            await axiosService.post(`${API_BASE}/api/public/admin/prospects/send-test-audit-email`, {
                first_name: previewFirstName || 'Elisa',
                url: previewUrl || 'https://www.example.com',
                score_global: previewScore !== '' ? Math.min(100, Math.max(0, Number(previewScore))) : 64,
            });
            setPreviewTestMessage({ type: 'success', text: 'Exemple d\'email envoyé à votre adresse. Vérifiez votre boîte de réception.' });
        } catch (e) {
            setPreviewTestMessage({ type: 'error', text: e.response?.data?.detail || 'Échec de l\'envoi.' });
        } finally {
            setSendTestAuditLoading(false);
        }
    };

    const isGmail = (smtpForm.smtp_host || '').toLowerCase().includes('gmail');

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center py-20">
                <Loader2 size={28} className="animate-spin text-accent mb-4" />
                <p className="text-sm text-blue-gray-500">Chargement de la configuration…</p>
            </div>
        );
    }

    const isKeysSection = configSection === 'keys';

    return (
        <div className="w-full space-y-6">
            {isKeysSection ? (
                <>
                    <div className="rounded-2xl border border-blue-gray-100 bg-gradient-to-br from-white via-blue-gray-50/40 to-accent/[0.04] p-6 md:p-8 shadow-sm">
                        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                            <div>
                                <h2 className="font-heading text-xl font-bold text-blue-gray-900">Clés API & accès</h2>
                                <p className="text-sm text-blue-gray-500 mt-1.5 max-w-xl">
                                    PageSpeed et Gemini alimentent les audits gratuits. Les compteurs ci-dessous reflètent l’usage dans cette app ; les quotas Google réels sont sur la console Google et AI Studio.
                                </p>
                            </div>
                            <div className="flex flex-wrap gap-2">
                                <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border ${config?.pagespeed_configured ? 'bg-amber-50 text-amber-800 border-amber-200' : 'bg-blue-gray-100 text-blue-gray-600 border-blue-gray-200'}`}>
                                    <Gauge size={13} />
                                    PageSpeed {config?.pagespeed_configured ? 'OK' : 'à configurer'}
                                </span>
                                <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border ${config?.gemini_configured ? 'bg-violet-50 text-violet-800 border-violet-200' : 'bg-blue-gray-100 text-blue-gray-600 border-blue-gray-200'}`}>
                                    <KeyRound size={13} />
                                    Gemini {config?.gemini_configured ? 'OK' : 'à configurer'}
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card>
                    <div className="flex items-start gap-3 mb-4">
                        <div className="w-9 h-9 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
                            <Shield size={18} className="text-accent" />
                        </div>
                        <div>
                            <h2 className="font-heading font-semibold text-blue-gray-900 text-base">Accès à la liste des prospects</h2>
                            <p className="text-sm text-blue-gray-500 mt-0.5">
                                Détermine qui peut voir et traiter les prospects dans l'application.
                            </p>
                        </div>
                    </div>
                    <fieldset className="space-y-2">
                        <legend className="sr-only">Qui a accès</legend>
                        <label
                            className={`flex items-center gap-3 p-3.5 rounded-xl border-2 cursor-pointer transition-all duration-200 ${
                                access === 'all'
                                    ? 'border-accent bg-accent/5 shadow-sm'
                                    : 'border-blue-gray-100 hover:border-blue-gray-200 hover:bg-blue-gray-50/50'
                            }`}
                        >
                            <input
                                type="radio"
                                name="prospect_access"
                                checked={access === 'all'}
                                onChange={() => setAccess('all')}
                                className="sr-only"
                            />
                            <span
                                className={`flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
                                    access === 'all'
                                        ? 'border-accent bg-accent'
                                        : 'border-blue-gray-300 bg-white'
                                }`}
                                aria-hidden
                            >
                                {access === 'all' && <span className="w-1.5 h-1.5 rounded-full bg-white" />}
                            </span>
                            <span className="text-sm font-medium text-blue-gray-800">Tous les utilisateurs connectés</span>
                        </label>
                        <label
                            className={`flex items-center gap-3 p-3.5 rounded-xl border-2 cursor-pointer transition-all duration-200 ${
                                access === 'staff_only'
                                    ? 'border-accent bg-accent/5 shadow-sm'
                                    : 'border-blue-gray-100 hover:border-blue-gray-200 hover:bg-blue-gray-50/50'
                            }`}
                        >
                            <input
                                type="radio"
                                name="prospect_access"
                                checked={access === 'staff_only'}
                                onChange={() => setAccess('staff_only')}
                                className="sr-only"
                            />
                            <span
                                className={`flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
                                    access === 'staff_only'
                                        ? 'border-accent bg-accent'
                                        : 'border-blue-gray-300 bg-white'
                                }`}
                                aria-hidden
                            >
                                {access === 'staff_only' && <span className="w-1.5 h-1.5 rounded-full bg-white" />}
                            </span>
                            <span className="text-sm font-medium text-blue-gray-800">Uniquement les administrateurs</span>
                        </label>
                    </fieldset>
                </Card>

                <Card>
                    <div className="flex items-start gap-3">
                        <div className="w-9 h-9 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                            <Gauge size={18} className="text-amber-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap items-center gap-3">
                                <h2 className="font-heading font-semibold text-blue-gray-900 text-base m-0 leading-tight">Clé Google PageSpeed (audits gratuits)</h2>
                                {!loading && config != null && (
                                    <span className="inline-flex items-center">
                                        <Badge color={config.pagespeed_configured ? 'success' : 'warning'}>
                                            {config.pagespeed_configured ? 'Configurée' : 'Non configurée'}
                                        </Badge>
                                    </span>
                                )}
                                <button
                                    type="button"
                                    onClick={() => setConfigKeyModal('pagespeed')}
                                    className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-amber-100 text-amber-600 hover:bg-amber-200 transition-colors cursor-pointer flex-shrink-0"
                                    aria-label="Comment configurer la clé PageSpeed"
                                >
                                    <Info size={14} />
                                </button>
                            </div>
                            <p className="text-sm text-blue-gray-500 mt-1.5">
                                Utilisée pour les scores performance des audits gratuits. Le compteur ci-dessous indique les appels effectués par cette app ; pour l’usage réel de votre compte, consultez Google.
                            </p>
                            <p className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-3 text-xs">
                                <a href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-amber-600 hover:underline">
                                    Créer une clé API <ExternalLink size={12} />
                                </a>
                                <a href="https://console.cloud.google.com/apis/api/pagespeedonline.googleapis.com/quotas" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 font-medium text-amber-600 hover:underline">
                                    Voir l’usage réel et les quotas (compte Google) <ExternalLink size={12} />
                                </a>
                            </p>
                        </div>
                    </div>
                    <div className="border-t border-blue-gray-100 pt-5 mt-5 space-y-4">
                        <div>
                            <Input
                                label="Clé API PageSpeed Insights"
                                name="pagespeed_key"
                                type="password"
                                value={pagespeedKey}
                                onChange={(e) => setPagespeedKey(e.target.value)}
                                placeholder={config?.pagespeed_configured ? '•••••••• (laisser vide pour conserver)' : 'Collez votre clé API Google'}
                            />
                            <p className="text-xs text-blue-gray-500 mt-2">En enregistrant une nouvelle clé, le quota max est fixé à 25 000 (défaut Google) si non renseigné.</p>
                        </div>
                        <div className="pt-1">
                            <label className="block text-xs font-medium text-blue-gray-600 mb-1.5">Quota max (appels)</label>
                            <input
                                type="number"
                                min={0}
                                value={pagespeedQuotaLimit}
                                onChange={(e) => setPagespeedQuotaLimit(Number(e.target.value) || 0)}
                                className="w-full px-4 py-2.5 text-sm border border-blue-gray-200 rounded-lg outline-none focus:ring-2 focus:ring-accent/20"
                            />
                        </div>
                        <div className="py-1">
                            <QuotaProgressBar
                                used={config?.pagespeed_quota_used ?? 0}
                                limit={config?.pagespeed_quota_limit ?? pagespeedQuotaLimit ?? 25000}
                                label="Utilisé par cette app"
                            />
                        </div>
                        <p className="text-xs text-blue-gray-400 mt-2">Appels PageSpeed effectués par Ascanio pour les audits gratuits. L’usage réel de votre clé s’affiche dans le lien « Voir l’usage réel et les quotas » ci-dessus.</p>
                        {(config?.pagespeed_quota_used ?? 0) > 0 && (
                            <div className="flex justify-end pt-1">
                                <button
                                    type="button"
                                    onClick={async () => {
                                        try {
                                            await axiosService.put(`${API_BASE}/api/public/admin/prospects/config`, { reset_pagespeed_quota: true });
                                            fetchConfig();
                                        } catch (e) { setMessage({ type: 'error', text: e.response?.data?.detail || 'Erreur' }); }
                                    }}
                                    className="text-xs text-amber-600 hover:underline"
                                >
                                    Remise à zéro du compteur
                                </button>
                            </div>
                        )}
                    </div>
                </Card>

                <Card className="lg:col-span-2">
                    <div className="flex items-start gap-3 mb-4">
                        <div className="w-9 h-9 rounded-lg bg-violet-100 flex items-center justify-center flex-shrink-0">
                            <KeyRound size={18} className="text-violet-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                                <h2 className="font-heading font-semibold text-blue-gray-900 text-base">Clé Gemini (audits gratuits)</h2>
                                <button
                                    type="button"
                                    onClick={() => setConfigKeyModal('gemini')}
                                    className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-violet-100 text-violet-600 hover:bg-violet-200 transition-colors cursor-pointer"
                                    aria-label="Comment configurer la clé Gemini"
                                >
                                    <Info size={14} />
                                </button>
                            </div>
                            <p className="text-sm text-blue-gray-500 mt-0.5">
                                Utilisée pour l’analyse IA des audits gratuits (2 appels par audit). Le compteur ci-dessous indique les appels effectués par cette app ; pour l’usage réel, consultez Google AI Studio.
                            </p>
                            <p className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-xs">
                                <a href="https://aistudio.google.com/apikey" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-violet-600 hover:underline">
                                    Créer une clé API <ExternalLink size={12} />
                                </a>
                                <a href="https://aistudio.google.com/usage" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 font-medium text-violet-600 hover:underline">
                                    Voir l’usage réel et les quotas (compte Google) <ExternalLink size={12} />
                                </a>
                            </p>
                        </div>
                    </div>
                    <div className="space-y-3">
                        <Input
                            label="Clé API Gemini"
                            name="gemini_key"
                            type="password"
                            value={geminiKey}
                            onChange={(e) => setGeminiKey(e.target.value)}
                            placeholder={config?.gemini_configured ? '•••••••• (laisser vide pour conserver)' : 'Collez votre clé API Gemini'}
                        />
                        <p className="text-xs text-blue-gray-500">En enregistrant une nouvelle clé, le quota max est fixé à 1 500 appels si non renseigné.</p>
                        <div>
                            <label className="block text-xs font-medium text-blue-gray-600 mb-1.5">Quota max (appels)</label>
                            <input
                                type="number"
                                min={0}
                                value={geminiQuotaLimit}
                                onChange={(e) => setGeminiQuotaLimit(Number(e.target.value) || 0)}
                                className="w-full px-4 py-2.5 text-sm border border-blue-gray-200 rounded-lg outline-none focus:ring-2 focus:ring-accent/20"
                            />
                        </div>
                        <QuotaProgressBar
                            used={config?.gemini_quota_used ?? 0}
                            limit={config?.gemini_quota_limit ?? geminiQuotaLimit ?? 1500}
                            label="Utilisé par cette app"
                        />
                        <p className="text-xs text-blue-gray-400">Appels Gemini effectués par Ascanio pour les audits gratuits. L’usage réel de votre clé s’affiche dans le lien « Voir l’usage réel et les quotas » ci-dessus.</p>
                        {(config?.gemini_quota_used ?? 0) > 0 && (
                            <div className="flex justify-end">
                                <button
                                    type="button"
                                    onClick={async () => {
                                        try {
                                            await axiosService.put(`${API_BASE}/api/public/admin/prospects/config`, { reset_gemini_quota: true });
                                            fetchConfig();
                                        } catch (e) { setMessage({ type: 'error', text: e.response?.data?.detail || 'Erreur' }); }
                                    }}
                                    className="text-xs text-amber-600 hover:underline"
                                >
                                    Remise à zéro du compteur
                                </button>
                            </div>
                        )}
                    </div>

                    {/* ── Diagnostic disponibilité des modèles ── */}
                    <GeminiModelProbe />
                </Card>
                    </div>
                </>
            ) : (
                <>
                    <div className="rounded-2xl border border-blue-gray-100 bg-gradient-to-br from-white via-teal-50/30 to-accent/[0.04] p-6 md:p-8 shadow-sm">
                        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                            <div>
                                <h2 className="font-heading text-xl font-bold text-blue-gray-900">Emails & envoi</h2>
                                <p className="text-sm text-blue-gray-500 mt-1.5 max-w-2xl">
                                    Destinataires des alertes leads, serveur SMTP et liens utilisés dans les messages aux prospects.
                                </p>
                            </div>
                            <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border self-start ${config?.smtp_configured ? 'bg-green-50 text-green-800 border-green-200' : 'bg-amber-50 text-amber-800 border-amber-200'}`}>
                                <MailCheck size={13} />
                                SMTP {config?.smtp_configured ? 'configuré' : 'à configurer'}
                            </span>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <Card>
                            <div className="flex items-start gap-3 mb-4">
                                <div className="w-9 h-9 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
                                    <Mailbox size={18} className="text-accent" />
                                </div>
                                <div>
                                    <h2 className="font-heading font-semibold text-blue-gray-900 text-base">Emails de nouveaux leads</h2>
                                    <p className="text-sm text-blue-gray-500 mt-0.5">
                                        Ces adresses reçoivent un email à chaque nouvelle demande d&apos;audit (en plus de la notification dans l&apos;app).
                                    </p>
                                </div>
                            </div>
                            <label className="block text-xs font-medium text-blue-gray-600 mb-2">Adresses à notifier</label>
                            <textarea
                                className="w-full px-4 py-3 text-sm border border-blue-gray-200 rounded-lg resize-y min-h-[100px] outline-none focus:ring-2 focus:ring-accent/20 focus:border-accent/40 bg-white transition-shadow"
                                placeholder="ex: equipe@mon-agence.com&#10;commercial@mon-agence.com"
                                value={leadEmailsText}
                                onChange={(e) => setLeadEmailsText(e.target.value)}
                                rows={4}
                                aria-describedby="lead-emails-hint"
                            />
                            <p id="lead-emails-hint" className="text-xs text-blue-gray-400 mt-2">Une adresse par ligne, ou séparées par une virgule. Les emails ne partent que si le SMTP ci-dessous est configuré.</p>
                        </Card>
                    </div>

                    <Card>
                    <div className="flex items-start gap-3 mb-4">
                        <div className="w-9 h-9 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
                            <Mail size={18} className="text-accent" />
                        </div>
                        <div>
                            <h2 className="font-heading font-semibold text-blue-gray-900 text-base">Serveur SMTP & expéditeur</h2>
                            <p className="text-sm text-blue-gray-500 mt-0.5">
                                Utilisé pour les emails envoyés aux prospects (confirmation d&apos;audit, résultats, envoi manuel).
                            </p>
                        </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <Input
                            label="Serveur SMTP"
                            name="smtp_host"
                            value={smtpForm.smtp_host}
                            onChange={(e) => setSmtpForm((f) => ({ ...f, smtp_host: e.target.value }))}
                            placeholder="smtp.gmail.com"
                        />
                        <Input
                            label="Port"
                            name="smtp_port"
                            type="number"
                            value={smtpForm.smtp_port}
                            onChange={(e) => setSmtpForm((f) => ({ ...f, smtp_port: parseInt(e.target.value, 10) || 587 }))}
                            placeholder="587"
                        />
                        <Input
                            label="Utilisateur SMTP"
                            name="smtp_user"
                            value={smtpForm.smtp_user}
                            onChange={(e) => setSmtpForm((f) => ({ ...f, smtp_user: e.target.value }))}
                            placeholder="votre@email.com"
                        />
                        <div>
                            <div className="flex items-start gap-2">
                                <div className="flex-1 min-w-0">
                                    <Input
                                        label="Mot de passe SMTP"
                                        name="smtp_password"
                                        type="password"
                                        value={smtpForm.smtp_password}
                                        onChange={(e) => setSmtpForm((f) => ({ ...f, smtp_password: e.target.value }))}
                                        placeholder={config?.smtp_configured ? 'Laisser vide pour conserver l\'actuel' : 'Mot de passe ou mot de passe d\'application'}
                                    />
                                </div>
                                {isGmail && (
                                    <Tooltip
                                        content="Pour Gmail/Google Workspace : utilisez un mot de passe d'application, pas le mot de passe du compte. Créez-en un dans votre compte Google → Sécurité → Validation en 2 étapes → Mots de passe des applications. Enregistrez ensuite la config en saisissant ce mot de passe ci‑dessus."
                                        className="flex-shrink-0 mt-9"
                                    >
                                        <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-amber-100 text-amber-600 cursor-help" aria-label="Info Gmail">
                                            <Info size={18} />
                                        </span>
                                    </Tooltip>
                                )}
                            </div>
                        </div>
                        <Input
                            label="Email d'envoi (From)"
                            name="smtp_from"
                            value={smtpForm.smtp_from}
                            onChange={(e) => setSmtpForm((f) => ({ ...f, smtp_from: e.target.value }))}
                            placeholder="noreply@votredomaine.fr"
                        />
                        <Input
                            label="Nom affiché (From Name)"
                            name="smtp_from_name"
                            value={smtpForm.smtp_from_name}
                            onChange={(e) => setSmtpForm((f) => ({ ...f, smtp_from_name: e.target.value }))}
                            placeholder="Ascanio"
                        />
                        <div className="md:col-span-2">
                            <Input
                                label="URL publique de l'app (pour liens audit)"
                                name="public_base_url"
                                value={smtpForm.public_base_url}
                                onChange={(e) => setSmtpForm((f) => ({ ...f, public_base_url: e.target.value }))}
                                placeholder="https://app.scanio.fr"
                            />
                        </div>
                        <div className="md:col-span-2">
                            <Input
                                label="URL cible des CTA (audit gratuit)"
                                name="audit_cta_url"
                                value={smtpForm.audit_cta_url}
                                onChange={(e) => setSmtpForm((f) => ({ ...f, audit_cta_url: e.target.value }))}
                                placeholder="https://calendar.google.com/calendar/u/0/appointments/..."
                            />
                            <p className="text-xs text-blue-gray-400 mt-2">
                                Tous les CTA des pages d&apos;audit gratuit pointeront vers cette URL.
                            </p>
                        </div>
                    </div>
                </Card>

            {/* Aperçu de l'email « Votre audit est prêt » */}
            <Card className="mt-6">
                <div className="flex items-start gap-3 mb-4">
                    <div className="w-9 h-9 rounded-lg bg-teal-100 flex items-center justify-center flex-shrink-0">
                        <Mail size={18} className="text-teal-600" />
                    </div>
                    <div>
                        <h2 className="font-heading font-semibold text-blue-gray-900 text-base">Aperçu de l'email « Votre audit est prêt »</h2>
                        <p className="text-sm text-blue-gray-500 mt-0.5">
                            Prévisualisez et envoyez un exemple à votre adresse pour vérifier le rendu.
                        </p>
                    </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    <Input
                        label="Prénom (exemple)"
                        value={previewFirstName}
                        onChange={(e) => setPreviewFirstName(e.target.value)}
                        placeholder="Elisa"
                    />
                    <Input
                        label="URL (exemple)"
                        value={previewUrl}
                        onChange={(e) => setPreviewUrl(e.target.value)}
                        placeholder="https://www.example.com"
                    />
                    <div>
                        <label className="block text-xs font-medium text-blue-gray-600 mb-1.5">Score (0-100)</label>
                        <input
                            type="number"
                            min={0}
                            max={100}
                            value={previewScore}
                            onChange={(e) => setPreviewScore(e.target.value)}
                            className="w-full px-4 py-2.5 text-sm border border-blue-gray-200 rounded-lg outline-none focus:ring-2 focus:ring-accent/20 focus:border-accent/40"
                        />
                    </div>
                </div>
                <div className="flex flex-wrap items-center gap-3 mb-4">
                    <Button type="button" variant="secondary" onClick={fetchPreviewEmail} disabled={previewLoading} className="flex items-center gap-2">
                        {previewLoading ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />}
                        Mettre à jour l'aperçu
                    </Button>
                    <Button type="button" onClick={sendTestAuditEmail} disabled={sendTestAuditLoading || !config?.smtp_configured} className="flex items-center gap-2">
                        {sendTestAuditLoading ? <Loader2 size={16} className="animate-spin" /> : <MailCheck size={16} />}
                        Envoyer un exemple à mon adresse
                    </Button>
                    {!config?.smtp_configured && (
                        <span className="text-xs text-amber-600">Configurez le SMTP ci-dessus pour pouvoir envoyer un exemple.</span>
                    )}
                </div>
                {previewTestMessage && (
                    <div
                        role="alert"
                        className={`mb-4 px-4 py-3 rounded-lg text-sm flex items-center gap-2 ${previewTestMessage.type === 'success'
                            ? 'bg-green-50 text-green-800 border border-green-200'
                            : 'bg-red-50 text-red-800 border border-red-200'
                        }`}
                    >
                        {previewTestMessage.type === 'success' ? <CheckCircle size={18} className="flex-shrink-0" /> : <XCircle size={18} className="flex-shrink-0" />}
                        {previewTestMessage.text}
                    </div>
                )}
                {previewEmailHtml ? (
                    <div className="border border-blue-gray-200 rounded-xl overflow-hidden bg-white">
                        <div className="px-3 py-2 border-b border-blue-gray-100 bg-blue-gray-50/50 text-xs font-medium text-blue-gray-500">
                            Rendu de l'email (lien « Voir mon rapport complet » non fonctionnel en aperçu)
                        </div>
                        <iframe
                            title="Aperçu email audit prêt"
                            srcDoc={previewEmailHtml}
                            className="w-full min-h-[480px] border-0"
                            sandbox="allow-same-origin"
                            style={{ height: '560px' }}
                        />
                    </div>
                ) : (
                    <div className="py-12 text-center text-blue-gray-400 text-sm">
                        Cliquez sur « Mettre à jour l'aperçu » pour afficher l'email.
                    </div>
                )}
            </Card>
                </>
            )}

            {message && (
                <div
                    role="alert"
                    className={`px-4 py-3 rounded-lg text-sm flex items-center gap-2 ${message.type === 'success'
                        ? 'bg-green-50 text-green-800 border border-green-200'
                        : 'bg-red-50 text-red-800 border border-red-200'
                    }`}
                >
                    {message.type === 'success' ? <CheckCircle size={18} className="flex-shrink-0" /> : <XCircle size={18} className="flex-shrink-0" />}
                    {message.text}
                </div>
            )}
            {testMessage && (
                <div
                    role="alert"
                    className={`px-4 py-3 rounded-lg text-sm flex items-center gap-2 ${testMessage.type === 'success'
                        ? 'bg-green-50 text-green-800 border border-green-200'
                        : 'bg-red-50 text-red-800 border border-red-200'
                    }`}
                >
                    {testMessage.type === 'success' ? <CheckCircle size={18} className="flex-shrink-0" /> : <XCircle size={18} className="flex-shrink-0" />}
                    {testMessage.text}
                </div>
            )}

            <div className="flex flex-wrap items-center gap-3 pt-4 mt-2 border-t border-blue-gray-100">
                <Button onClick={handleSave} disabled={saving} className="flex items-center gap-2">
                    {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                    Enregistrer
                </Button>
                {configSection === 'emails' && (
                    <Button type="button" variant="secondary" onClick={handleTestSend} disabled={testLoading || saving} className="flex items-center gap-2">
                        {testLoading ? <Loader2 size={16} className="animate-spin" /> : <MailCheck size={16} />}
                        Tester l&apos;envoi SMTP
                    </Button>
                )}
                <span className="text-xs text-blue-gray-400 max-w-xl">
                    {configSection === 'keys'
                        ? 'Enregistre les clés PageSpeed, Gemini et les droits d’accès. Les emails et SMTP se gèrent dans l’onglet Emails.'
                        : 'Enregistrez d’abord si vous avez modifié le mot de passe SMTP. Le test envoie un email à votre adresse utilisateur.'}
                </span>
            </div>

            <Modal open={configKeyModal === 'pagespeed'} onClose={() => setConfigKeyModal(null)} title="Comment configurer la clé Google PageSpeed" size="md">
                <ol className="list-decimal list-inside space-y-2 text-sm text-blue-gray-700">
                    <li>Ouvrez la <a href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noopener noreferrer" className="text-amber-600 hover:underline inline-flex items-center gap-1">Google Cloud Console → Identifiants <ExternalLink size={12} /></a>.</li>
                    <li>Activez l’API « PageSpeed Insights API » (APIs & Services → Bibliothèque).</li>
                    <li>Créez des identifiants → Clé API, puis copiez la clé.</li>
                    <li>Collez la clé dans le champ « Clé API PageSpeed Insights » ci-dessus.</li>
                </ol>
                <p className="mt-4 text-sm text-blue-gray-600">Quota gratuit : environ 25 000 requêtes / jour. Vous pouvez restreindre la clé à l’API PageSpeed Insights uniquement pour plus de sécurité.</p>
            </Modal>

            <Modal open={configKeyModal === 'gemini'} onClose={() => setConfigKeyModal(null)} title="Comment configurer la clé Gemini" size="md">
                <ol className="list-decimal list-inside space-y-2 text-sm text-blue-gray-700">
                    <li>Allez sur <a href="https://aistudio.google.com/apikey" target="_blank" rel="noopener noreferrer" className="text-violet-600 hover:underline inline-flex items-center gap-1">Google AI Studio <ExternalLink size={12} /></a>.</li>
                    <li>Connectez-vous avec votre compte Google.</li>
                    <li>Cliquez sur « Obtenir une clé API » ou « Create API key », puis créez une clé.</li>
                    <li>Collez la clé dans le champ « Clé API Gemini » ci-dessus.</li>
                </ol>
                <p className="mt-4 text-sm text-blue-gray-600">Chaque audit gratuit utilise 2 appels API (analyse IA + interprétations des onglets). Ajustez le quota max en conséquence.</p>
            </Modal>
        </div>
    );
}

// ── Pill de source (liste CSV) avec actions hover ──

function ListSourcePill({ list, active, onSelect, onRename, onDelete }) {
    const [renaming, setRenaming] = useState(false);
    const [renameVal, setRenameVal] = useState('');
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [deleteWithAudits, setDeleteWithAudits] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);

    const startRename = (e) => {
        e.stopPropagation();
        setRenameVal(list.name);
        setRenaming(true);
    };
    const submitRename = async () => {
        if (renameVal.trim()) await onRename(list.id, renameVal.trim());
        setRenaming(false);
    };

    if (renaming) {
        return (
            <div className="flex items-center gap-1 px-2 py-0.5 rounded-full border border-accent/40 bg-white">
                <input
                    autoFocus
                    className="text-xs outline-none w-28"
                    value={renameVal}
                    onChange={e => setRenameVal(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') submitRename(); if (e.key === 'Escape') setRenaming(false); }}
                    onBlur={submitRename}
                    maxLength={100}
                />
            </div>
        );
    }

    return (
        <>
            <div className="relative group">
                <button
                    type="button"
                    onClick={onSelect}
                    className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium transition-all ${active ? 'bg-blue-gray-800 text-white shadow-sm' : 'bg-white border border-blue-gray-200 text-blue-gray-500 hover:border-blue-gray-400 hover:text-blue-gray-700'}`}
                >
                    <FileText size={11} />
                    {list.name}
                    <span className={`text-[10px] ${active ? 'text-white/60' : 'text-blue-gray-300'}`}>({list.prospect_count})</span>
                </button>
                {/* Actions au survol */}
                <div className="hidden group-hover:flex items-center gap-0.5 absolute -top-1 -right-1 bg-white border border-blue-gray-100 rounded-full shadow-md px-1 py-0.5 z-10">
                    <button type="button" onClick={startRename} className="p-0.5 text-blue-gray-400 hover:text-accent transition rounded-full" title="Renommer">
                        <Pencil size={10} />
                    </button>
                    <button type="button" onClick={e => { e.stopPropagation(); setConfirmDelete(true); setDeleteWithAudits(false); }} className="p-0.5 text-blue-gray-400 hover:text-red-500 transition rounded-full" title="Supprimer">
                        <Trash2 size={10} />
                    </button>
                </div>
            </div>

            <Modal
                open={confirmDelete}
                onClose={() => !deleteLoading && setConfirmDelete(false)}
                title="Supprimer cette liste ?"
                size="sm"
            >
                <p className="text-sm text-blue-gray-600 mb-4">
                    Supprimer <span className="font-semibold">"{list.name}"</span> ({list.prospect_count} prospect{list.prospect_count !== 1 ? 's' : ''}).
                </p>
                <label className="flex items-center gap-2 text-sm text-blue-gray-600 mb-5 cursor-pointer">
                    <input type="checkbox" checked={deleteWithAudits} onChange={e => setDeleteWithAudits(e.target.checked)} className="accent-red-500" />
                    Supprimer aussi les {list.prospect_count} audit{list.prospect_count !== 1 ? 's' : ''} associés
                </label>
                <div className="flex justify-end gap-2">
                    <Button variant="secondary" onClick={() => setConfirmDelete(false)} disabled={deleteLoading}>Annuler</Button>
                    <Button
                        variant="primary"
                        onClick={async () => {
                            setDeleteLoading(true);
                            try { await onDelete(list.id, deleteWithAudits); setConfirmDelete(false); }
                            finally { setDeleteLoading(false); }
                        }}
                        disabled={deleteLoading}
                        className="!bg-red-500 !border-red-500"
                    >
                        {deleteLoading ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
                        Supprimer
                    </Button>
                </div>
            </Modal>
        </>
    );
}

// ── Page principale ──

const ALL_PROSPECT_TABS = [
    { key: 'demandes', label: 'Prospects', icon: <Users size={20} />, roles: ['admin', 'commercial'] },
    { key: 'comportement', label: 'Statistiques', icon: <Activity size={20} />, roles: ['admin', 'commercial'] },
    { key: 'cles_api', label: 'Clés API', icon: <KeyRound size={20} />, roles: ['admin'] },
    { key: 'emails', label: 'Emails', icon: <Mail size={20} />, roles: ['admin'] },
];

// ── Barre de sous-onglets pour l'onglet Prospects ──

function ProspectSubTabs({ lists, activeListId, onSelectList, onRename, onDelete, onOpenImport }) {
    const [renamingId, setRenamingId] = useState(null);
    const [renameValue, setRenameValue] = useState('');
    const [confirmDeleteId, setConfirmDeleteId] = useState(null);
    const [deleteWithAudits, setDeleteWithAudits] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);

    const startRename = (list, e) => {
        e.stopPropagation();
        setRenamingId(list.id);
        setRenameValue(list.name);
    };

    const submitRename = async (id) => {
        if (!renameValue.trim()) { setRenamingId(null); return; }
        await onRename(id, renameValue.trim());
        setRenamingId(null);
    };

    return (
        <>
            <div className="flex items-center gap-1 flex-wrap border-b border-blue-gray-100 pb-0 mb-4">
                {/* Sous-onglet Tunnel */}
                <button
                    type="button"
                    onClick={() => onSelectList(null)}
                    className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 transition-all whitespace-nowrap ${activeListId === null ? 'border-accent text-accent' : 'border-transparent text-blue-gray-500 hover:text-blue-gray-800'}`}
                >
                    <Globe size={14} />
                    Tunnel
                </button>

                {/* Sous-onglets listes dynamiques */}
                {lists.map(list => (
                    <div key={list.id} className="relative flex items-center group">
                        {renamingId === list.id ? (
                            <div className="flex items-center gap-1 px-2 py-1">
                                <input
                                    autoFocus
                                    className="text-sm border border-accent/40 rounded px-2 py-0.5 outline-none focus:ring-2 focus:ring-accent/20 w-36"
                                    value={renameValue}
                                    onChange={e => setRenameValue(e.target.value)}
                                    onKeyDown={e => { if (e.key === 'Enter') submitRename(list.id); if (e.key === 'Escape') setRenamingId(null); }}
                                    onBlur={() => submitRename(list.id)}
                                    maxLength={100}
                                />
                            </div>
                        ) : (
                            <button
                                type="button"
                                onClick={() => onSelectList(list.id)}
                                className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 transition-all whitespace-nowrap ${activeListId === list.id ? 'border-accent text-accent' : 'border-transparent text-blue-gray-500 hover:text-blue-gray-800'}`}
                            >
                                <FileText size={13} />
                                <span>{list.name}</span>
                                <span className="text-[10px] text-blue-gray-400 ml-0.5">({list.prospect_count})</span>
                            </button>
                        )}
                        {/* Actions hover : renommer + supprimer */}
                        {renamingId !== list.id && (
                            <div className="hidden group-hover:flex items-center gap-0.5 absolute right-0 top-1/2 -translate-y-1/2 bg-white border border-blue-gray-100 rounded-md shadow-sm px-1 py-0.5 z-10">
                                <button
                                    type="button"
                                    onClick={e => startRename(list, e)}
                                    className="p-1 text-blue-gray-400 hover:text-accent transition rounded"
                                    title="Renommer"
                                >
                                    <Pencil size={11} />
                                </button>
                                <button
                                    type="button"
                                    onClick={e => { e.stopPropagation(); setConfirmDeleteId(list.id); setDeleteWithAudits(false); }}
                                    className="p-1 text-blue-gray-400 hover:text-red-500 transition rounded"
                                    title="Supprimer"
                                >
                                    <Trash2 size={11} />
                                </button>
                            </div>
                        )}
                    </div>
                ))}

                {/* Bouton + (importer CSV) */}
                <button
                    type="button"
                    onClick={onOpenImport}
                    className="flex items-center gap-1.5 px-3 py-2 text-sm text-accent hover:bg-accent/5 border border-dashed border-accent/40 hover:border-accent rounded-lg transition ml-1 whitespace-nowrap"
                    title="Importer une nouvelle liste CSV"
                >
                    <Upload size={13} />
                    Nouvelle liste
                </button>
            </div>

            {/* Modal confirmation suppression */}
            <Modal
                open={confirmDeleteId !== null}
                onClose={() => !deleteLoading && setConfirmDeleteId(null)}
                title="Supprimer cette liste ?"
                size="sm"
            >
                {confirmDeleteId !== null && (() => {
                    const list = lists.find(l => l.id === confirmDeleteId);
                    return (
                        <>
                            <p className="text-sm text-blue-gray-600 mb-4">
                                Vous êtes sur le point de supprimer la liste <strong>{list?.name}</strong> ({list?.prospect_count} prospect{list?.prospect_count !== 1 ? 's' : ''}).
                            </p>
                            <label className="flex items-center gap-2 text-sm text-blue-gray-600 mb-5 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={deleteWithAudits}
                                    onChange={e => setDeleteWithAudits(e.target.checked)}
                                    className="rounded"
                                />
                                Supprimer aussi tous les audits de cette liste
                            </label>
                            <div className="flex justify-end gap-2">
                                <Button variant="secondary" onClick={() => setConfirmDeleteId(null)} disabled={deleteLoading}>
                                    Annuler
                                </Button>
                                <Button
                                    variant="primary"
                                    disabled={deleteLoading}
                                    onClick={async () => {
                                        setDeleteLoading(true);
                                        await onDelete(confirmDeleteId, deleteWithAudits);
                                        setConfirmDeleteId(null);
                                        setDeleteLoading(false);
                                    }}
                                >
                                    {deleteLoading ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                                    Supprimer
                                </Button>
                            </div>
                        </>
                    );
                })()}
            </Modal>
        </>
    );
}

export default function ProspectsPage() {
    const [activeTab, setActiveTab] = useState('demandes');
    const [selectedProspect, setSelectedProspect] = useState(null);
    const [listRefreshKey, setListRefreshKey] = useState(0);
    const [currentUser, setCurrentUser] = useState(null);

    // Charger le user connecté pour filtrer les onglets selon le rôle
    useEffect(() => {
        import('./api').then(({ getCurrentUser }) => {
            getCurrentUser().then(u => setCurrentUser(u)).catch(() => {});
        });
    }, []);

    const userType = currentUser?.user_type || 'admin'; // fallback admin pour ne pas bloquer l'affichage
    const PROSPECT_TAB_DATA = ALL_PROSPECT_TABS.filter(t => t.roles.includes(userType));

    // ── Gestion des listes CSV ──
    const [prospectLists, setProspectLists] = useState([]);
    const [activeListId, setActiveListId] = useState(null); // null = Tunnel
    const [showCsvImport, setShowCsvImport] = useState(false);

    const fetchProspectLists = useCallback(async () => {
        try {
            const res = await axiosService.get(`${API_BASE}/api/public/admin/prospect-lists`);
            setProspectLists(res.data.lists || []);
        } catch (e) { console.error('Erreur chargement listes:', e); }
    }, []);

    useEffect(() => { fetchProspectLists(); }, [fetchProspectLists]);

    const handleRenameList = useCallback(async (id, name) => {
        try {
            await axiosService.patch(`${API_BASE}/api/public/admin/prospect-lists/${id}`, { name });
            setProspectLists(prev => prev.map(l => l.id === id ? { ...l, name } : l));
        } catch (e) { console.error('Erreur renommage:', e); }
    }, []);

    const handleDeleteList = useCallback(async (id, deleteAudits) => {
        try {
            await axiosService.delete(`${API_BASE}/api/public/admin/prospect-lists/${id}`, { params: { delete_audits: deleteAudits } });
            if (activeListId === id) setActiveListId(null);
            await fetchProspectLists();
            setListRefreshKey(k => k + 1);
        } catch (e) { console.error('Erreur suppression:', e); }
    }, [activeListId, fetchProspectLists]);

    const handleImportDone = useCallback(async (newListId) => {
        await fetchProspectLists();
        if (newListId) setActiveListId(newListId);
        setListRefreshKey(k => k + 1);
    }, [fetchProspectLists]);

    const needsSourceFilter = activeTab === 'demandes' || activeTab === 'comportement';

    return (
        <div className="mx-auto w-full px-2 md:px-0 flex flex-col gap-6">

            {/* ── En-tête unique : titre + barre de navigation ── */}
            <div className="flex flex-col gap-3">
                <div className="flex items-center justify-between gap-4 flex-wrap">
                    <div>
                        <h1 className="font-heading text-2xl font-bold text-blue-gray-900 leading-tight">
                            Prospects & Leads
                        </h1>
                        <p className="text-blue-gray-400 text-sm mt-0.5">
                            Gestion complète des prospects, tracking et conversion.
                        </p>
                    </div>
                </div>

                {/* Ligne de navigation principale */}
                <div className="flex flex-col gap-0">
                    <Tab
                        active={activeTab}
                        onChange={tab => {
                            setActiveTab(tab);
                            if (tab === 'demandes' || tab === 'comportement') fetchProspectLists();
                        }}
                        className="mb-0"
                        tabData={PROSPECT_TAB_DATA}
                    />

                    {/* Sélecteur de source — n'apparaît que si la vue le nécessite */}
                    {needsSourceFilter && (
                        <div className="flex items-center gap-2 mt-2 flex-wrap">
                            {/* Label contextuel */}
                            <span className="text-[11px] font-semibold text-blue-gray-400 uppercase tracking-wide shrink-0">
                                Source
                            </span>
                            <div className="flex items-center gap-1.5 flex-wrap">
                                {/* Tunnel (landing) */}
                                <button
                                    type="button"
                                    onClick={() => { setActiveListId(null); setSelectedProspect(null); setListRefreshKey(k => k + 1); }}
                                    className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium transition-all ${activeListId === null ? 'bg-blue-gray-800 text-white shadow-sm' : 'bg-white border border-blue-gray-200 text-blue-gray-500 hover:border-blue-gray-400 hover:text-blue-gray-700'}`}
                                >
                                    <Globe size={11} />
                                    Tunnel
                                </button>

                                {/* Listes CSV */}
                                {prospectLists.map(list => (
                                    <ListSourcePill
                                        key={list.id}
                                        list={list}
                                        active={activeListId === list.id}
                                        onSelect={() => { setActiveListId(list.id); setSelectedProspect(null); setListRefreshKey(k => k + 1); }}
                                        onRename={handleRenameList}
                                        onDelete={handleDeleteList}
                                    />
                                ))}

                                {/* Bouton import CSV */}
                                <button
                                    type="button"
                                    onClick={() => setShowCsvImport(true)}
                                    className="flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium text-accent border border-dashed border-accent/40 hover:border-accent hover:bg-accent/5 transition-all"
                                    title="Importer une liste CSV"
                                >
                                    <Upload size={11} />
                                    Importer
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <CsvImportModal
                open={showCsvImport}
                onClose={() => setShowCsvImport(false)}
                onImportDone={handleImportDone}
            />

            {activeTab === 'demandes' && (
                <DemandesTab
                    onSelectProspect={setSelectedProspect}
                    refetchKey={listRefreshKey}
                    onListRefresh={() => { setListRefreshKey(k => k + 1); fetchProspectLists(); }}
                    selectedToken={selectedProspect?.token ?? null}
                    prospectListId={activeListId}
                    tunnelOnly={activeListId === null}
                />
            )}
            {activeTab === 'comportement' && (
                <ComportementTab
                    prospectListId={activeListId}
                    tunnelOnly={activeListId === null}
                    onViewProspect={p => setSelectedProspect(p)}
                />
            )}
            {(activeTab === 'cles_api' || activeTab === 'emails') && (
                <ConfigurationTab section={activeTab === 'cles_api' ? 'keys' : 'emails'} />
            )}

            {selectedProspect && (
                <ProspectDetailPanel
                    prospect={selectedProspect}
                    userType={userType}
                    onClose={() => setSelectedProspect(null)}
                    onRefresh={() => {
                        setSelectedProspect(null);
                        setActiveTab('demandes');
                    }}
                    onProspectViewed={() => {
                        setListRefreshKey(k => k + 1);
                        setSelectedProspect(prev => prev ? { ...prev, lead_status: 'in_progress' } : null);
                    }}
                />
            )}
        </div>
    );
}
